import { expandSectorRemoval } from "../helpers/sectorRemoval";
import { useColorImage } from "../../data/useColorImages";
import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback
} from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Loading from "../helpers/Loading";
import { getColorById } from "../../data/getters";
import {
  parseUsedTables,
  generateDiagonalLines,
  toKantList,
  parseRigheAbs,
  computeWasteRects,
  extractTableMetrics,
  fmtNum,
  getTablePositions,
  findFreePosition
} from "../../modules/helpers/tableViewerUtil";
import { Button, Modal, Dropdown, Form } from "react-bootstrap";
import { useLanguage } from "../helpers/LanguageContext";
import "../../css/TableViewer.css";
import { IonIcon } from "@ionic/react";
import {
  trashOutline,
  arrowDownOutline,
  chevronBackOutline,
  chevronForwardOutline
} from "ionicons/icons";

export const TableViewerComponent = React.memo(function TableViewerComponent({
  onGenerate,
  workId,
  table,
  items,
  containerRef,
  onNext,
  onPrev,
  selectedItemPosition,
  setSelectedItemPosition,
  isEditing,
  setIsEditing,
  startEditing,
  editedTables,
  setEditedTables,
  editedItems,
  setEditedItems,
  setCreatedTables,
  setCreatedItems,
  setProcessedItems,
  setCurrentTableIndex,
  showNoTableText,
  setShowNoTableText,
  isLoading,
  selectedColorId,
  setSelectedColorId,
  createdTables,
  setIsExporting,
  isExporting,
  processedItems,
  runWithLock,
  saveLock,
  onDirty,
  hasDirty,
  saveDirtyItemsIfAny,
  colorsFromStore,
  exportMode,
  setExportMode,
  selectedColorIds,
  setSelectedColorIds,
  selectedTableIds,
  setSelectedTableIds,
  handleConfirmedExport,
  usedColors,
  usedTables,
  showExportModal,
  setShowExportModal,
  saveGeneratedTables,
  loadData,
  statusMessage,
  setIsLoading,
  showMessage,
  onSizeList,
  generatedSavePendingRef,
  placementItemId,
  setPlacementItemId,
  placementRotation,
  setPlacementRotation,
  placementForceRotate,
  setPlacementForceRotate,
  onReplaySingleTable
}) {
  const { t } = useLanguage();
  const scaleFactor = 0.24;

  const [selectedItemPositions, setSelectedItemPositions] = useState([]);
  const [debugRectInfo, setDebugRectInfo] = useState(null);

  useEffect(() => {
    if (placementItemId == null) return;

    setSelectedItemPositions([]);
    setSelectedItemPosition?.(null);
  }, [placementItemId, setSelectedItemPosition]);

  /*
   * Ezek mindig az utolsó, ténylegesen használt állapotot tartalmazzák.
   * Így két egymás utáni törlés között nem kell megvárni a React újrarenderelését.
   */
  const latestTablesRef = useRef(
    isEditing
      ? Array.isArray(editedTables)
        ? editedTables
        : []
      : Array.isArray(createdTables)
        ? createdTables
        : []
  );

  const latestItemsRef = useRef(
    isEditing
      ? Array.isArray(editedItems)
        ? editedItems
        : []
      : Array.isArray(processedItems)
        ? processedItems
        : Array.isArray(items)
          ? items
          : []
  );

  useEffect(() => {
    latestTablesRef.current = isEditing
      ? Array.isArray(editedTables)
        ? editedTables
        : []
      : Array.isArray(createdTables)
        ? createdTables
        : [];
  }, [isEditing, editedTables, createdTables]);

  useEffect(() => {
    latestItemsRef.current = isEditing
      ? Array.isArray(editedItems)
        ? editedItems
        : []
      : Array.isArray(processedItems)
        ? processedItems
        : Array.isArray(items)
          ? items
          : [];
  }, [isEditing, editedItems, processedItems, items]);

  const shiftSelectingRef = useRef(false);
  const placementReplayLockRef = useRef(false);
  const shiftSelectionModeRef = useRef(null);
  const suppressNextClickRef = useRef(false);
  const millings = useSelector((state) => state.millings);
  const DEFAULT_NEW_TABLE_TRIM = 10;

  const normalizeCutOrientationValue = (value) => {
    if (
      value === true ||
      value === 1 ||
      value === "1" ||
      String(value).trim().toLowerCase() === "true" ||
      String(value).trim().toLowerCase() === "vertical" ||
      String(value).trim().toLowerCase() === "v"
    ) {
      return true;
    }

    return false;
  };

  const [addTableModal, setAddTableModal] = useState({
    show: false,
    size: null,
    isHalfTable: false
  });

  const [newTableTrim, setNewTableTrim] = useState(DEFAULT_NEW_TABLE_TRIM);
  const [newTableCutOrientation, setNewTableCutOrientation] = useState(false);
  const getMillingName = useCallback(
    (millingId) => {
      if (!millingId) return "";
      const m = millings.find((m) => m.millingId === millingId);
      return m?.name || "";
    },
    [millings]
  );
  const isMDFColor = useCallback(
    (colorId) => {
      const c = colorsFromStore.find((c) => c.colorId === colorId);
      return (
        String(c?.materialType ?? "")
          .trim()
          .toUpperCase() === "MDF"
      );
    },
    [colorsFromStore]
  );

  const BORDER_WIDTH = 2;
  const KERF = 4.4; // mm
  const TRIM = Math.max(0, Number(table?.trim ?? table?.TRIM ?? 10) || 0);
  const DETAIL_THRESHOLD = 80;
  const VERY_DETAIL_THRESHOLD = 40;
  const drawingRef = useRef(null);
  const canvasAreaRef = useRef(null);
  const tableColor = useSelector((state) =>
    table?.color?.colorId == null
      ? null
      : getColorById(table.color.colorId)(state)
  );
  const fullTableColor = useColorImage(tableColor);
  const bgImageBase64 =
    fullTableColor?.imageData || tableColor?.imageDataReduced;
  const tableSize = useMemo(
    () => JSON.parse(table?.size || "[0,0]"),
    [table?.size]
  );
  const visibleTables = useMemo(() => {
    if (isEditing) {
      return Array.isArray(editedTables) ? editedTables : [];
    }

    return Array.isArray(createdTables) ? createdTables : [];
  }, [isEditing, editedTables, createdTables]);

  const filteredTables = useMemo(() => {
    return visibleTables
      .filter((currentTable) => !isMDFColor(currentTable.color?.colorId))
      .filter((currentTable) =>
        selectedColorId != null
          ? String(currentTable.color?.colorId) === String(selectedColorId)
          : true
      );
  }, [visibleTables, selectedColorId, isMDFColor]);

  const tableRotation = -90;
  const showNav = filteredTables.length > 1;
  const allWorks = useSelector((state) =>
    Array.isArray(state.works)
      ? state.works
      : state.works?.work
        ? [state.works.work]
        : []
  );
  const serializeProcessedPositions = useCallback((positions) => {
    return (Array.isArray(positions) ? positions : [])
      .map((position) => {
        if (position.id == null) {
          throw new Error("tableId is null during manual placement");
        }

        const datiRowNumber = Number(position.datiRowNumber);
        const safeDatiRowNumber =
          Number.isInteger(datiRowNumber) && datiRowNumber > 0
            ? datiRowNumber
            : 0;

        return (
          `[${position.x},${position.y},${position.rotation},` +
          `${position.id},${safeDatiRowNumber}]`
        );
      })
      .join(",");
  }, []);

  const getNextManualInstanceId = useCallback((item) => {
    const usedInstanceIds = new Set(
      (item?.processedPositions || []).map((position) =>
        String(position.instanceId)
      )
    );

    let nextIndex = 0;
    let candidate = `${item.itemId}_${nextIndex}`;

    while (usedInstanceIds.has(candidate)) {
      nextIndex += 1;
      candidate = `${item.itemId}_${nextIndex}`;
    }

    return candidate;
  }, []);

  const selectedWork = allWorks.find((w) => w.workId == workId);
  const isOrdered = selectedWork?.isOrdered;
  const navigate = useNavigate();
  const [showLeaveGuard, setShowLeaveGuard] = useState(false);
  const [pendingPath, setPendingPath] = useState(null);
  const [showBackground, setShowBackground] = useState(true);

  const toggleBackground = () => setShowBackground((b) => !b);
  const currentUser = useSelector((state) => state.auth.user);
  // a komponens elején, az effectek fölött
  const tablePalette = useMemo(() => {
    const map = new Map();
    visibleTables.forEach((t) => {
      const cid = t.color?.colorId;
      if (cid != null) {
        // megkeressük a store‑ból a teljes color objektumot
        const fullColor = colorsFromStore.find((c) => c.colorId === cid);
        if (fullColor) map.set(cid, fullColor);
      }
    });
    return Array.from(map.values());
  }, [visibleTables, colorsFromStore]);
  const activeColorId =
    selectedColorId ??
    table?.color?.colorId ??
    tablePalette[0]?.colorId ??
    null;

  const activeColor = useMemo(() => {
    return (
      tablePalette.find((color) => color.colorId === activeColorId) ||
      tablePalette[0] ||
      null
    );
  }, [tablePalette, activeColorId]);
  const activeMillingName = useMemo(() => {
    const millingId = table?.milling?.millingId ?? table?.millingId ?? null;

    return getMillingName(millingId);
  }, [table?.milling?.millingId, table?.millingId, getMillingName]);

  const activeColorPaginationLabel = activeColor
    ? `${t(activeColor.name)}${
        activeMillingName ? ` - ${activeMillingName}` : ""
      }`
    : t("allColors");
  const changeColor = useCallback(
    (direction) => {
      if (tablePalette.length === 0) return;

      const currentIndex = tablePalette.findIndex(
        (color) => color.colorId === activeColorId
      );

      const safeCurrentIndex = currentIndex >= 0 ? currentIndex : 0;

      const nextIndex =
        (safeCurrentIndex + direction + tablePalette.length) %
        tablePalette.length;

      const nextColor = tablePalette[nextIndex];

      setSelectedColorId(nextColor.colorId);
      setCurrentTableIndex(0);
    },
    [tablePalette, activeColorId, setSelectedColorId, setCurrentTableIndex]
  );
  const workInfo = {
    id: selectedWork?.workId,
    user: selectedWork?.user?.name || "",
    usedTables: parseUsedTables(selectedWork?.usedTables || "[]"),
    tableUsage: selectedWork?.tableUsage ?? 0,
    cutLength: selectedWork?.cutLength ?? 0,
    partCount: selectedWork?.partCount ?? 0,
    measureCount: selectedWork?.measureCount ?? 0,
    woodPrice: selectedWork?.woodPrice ?? 0,
    kantLength: selectedWork?.kantLength || ""
  };
  // --- Aktuális tábla infók ---
  const tblInfo = useMemo(
    () => extractTableMetrics(table, colorsFromStore),
    [table, colorsFromStore]
  );
  const cannotEdit =
    (currentUser?.role === "user" && isOrdered) ||
    ((currentUser?.role === "companyAdmin" ||
      currentUser?.role === "companyUser") &&
      selectedWork?.companyStatus === "Completed") ||
    ((currentUser?.role === "companyAdmin" ||
      currentUser?.role === "companyUser") &&
      !isOrdered);

  const normalizeTableDimension = (value) => {
    if (value == null || value === "") return null;

    let parsed;

    try {
      parsed = Array.isArray(value) ? value : JSON.parse(String(value));
    } catch {
      const parts = String(value)
        .replace("[", "")
        .replace("]", "")
        .split(",")
        .map((part) => Number(part.trim()));

      parsed = parts;
    }

    if (!Array.isArray(parsed) || parsed.length < 2) {
      return null;
    }

    const height = Number(parsed[0]);
    const width = Number(parsed[1]);

    if (!(height > 0) || !(width > 0)) {
      return null;
    }

    return JSON.stringify([height, width]);
  };

  const shrinkTableDimension = (value, amount = 20) => {
    const normalized = normalizeTableDimension(value);

    if (!normalized) return null;

    const [height, width] = JSON.parse(normalized);

    const nextHeight = height - amount;
    const nextWidth = width - amount;

    if (!(nextHeight > 0) || !(nextWidth > 0)) {
      return null;
    }

    return JSON.stringify([nextHeight, nextWidth]);
  };

  const formatTableDimension = (value) => {
    const normalized = normalizeTableDimension(value);

    if (!normalized) return "";

    const [height, width] = JSON.parse(normalized);

    return `${height} × ${width} mm`;
  };

  const itemColorIds = Array.from(
    new Set(
      (Array.isArray(items) && items.length > 0
        ? items
        : Array.isArray(processedItems) && processedItems.length > 0
          ? processedItems
          : editedItems
      )
        .map((item) => item?.color?.colorId ?? item?.colorId ?? null)
        .filter((colorId) => colorId != null)
    )
  );
  const firstItemColorId = itemColorIds[0] ?? null;
  const currentTableColorId =
    table?.color?.colorId ??
    table?.colorId ??
    selectedColorId ??
    firstItemColorId ??
    null;

  const currentTableColor =
    currentTableColorId == null
      ? null
      : ((colorsFromStore || []).find(
          (color) => String(color.colorId) === String(currentTableColorId)
        ) ?? null);

  const currentFullDimension = normalizeTableDimension(
    currentTableColor?.dimension
  );

  const currentSplitDimension = normalizeTableDimension(
    currentTableColor?.splitDimension
  );

  const canAddHalfTable = currentSplitDimension != null;
  const clearPlacementSelection = useCallback(() => {
    setPlacementItemId(null);
    setPlacementRotation?.(null);
    setPlacementForceRotate?.(false);
  }, [setPlacementItemId, setPlacementRotation, setPlacementForceRotate]);

  const parseManualPlacementKant = (raw) => {
    if (!raw) {
      return {
        type: "-",
        longCount: 0,
        shortCount: 0
      };
    }

    try {
      let value = String(raw).trim();

      if (value.startsWith("[") && value.endsWith("]")) {
        value = value.slice(1, -1);
      }

      const parts = value.split(",");

      return {
        type: parts[0]?.trim() || "-",
        longCount: Number.parseInt(parts[1] ?? "0", 10) || 0,
        shortCount: Number.parseInt(parts[2] ?? "0", 10) || 0
      };
    } catch {
      return {
        type: "-",
        longCount: 0,
        shortCount: 0
      };
    }
  };
  const getSectorCascadeAxis = useCallback((activeTable) => {
    const rawOrientation =
      activeTable?.cutOrientation ??
      activeTable?.cutorientation ??
      activeTable?.cut_orientation ??
      null;

    if (rawOrientation == null) return null;

    /*
     * A generálásnál:
     * true  = VERTICAL
     * false = HORIZONTAL
     */
    if (rawOrientation === true) {
      return "x";
    }

    if (rawOrientation === false) {
      return "y";
    }

    const orientation = String(rawOrientation).trim().toLowerCase();

    if (
      orientation === "false" ||
      orientation === "0" ||
      orientation === "horizontal" ||
      orientation === "h"
    ) {
      return "y";
    }

    if (
      orientation === "true" ||
      orientation === "1" ||
      orientation === "vertical" ||
      orientation === "v"
    ) {
      return "x";
    }

    return null;
  }, []);
  const getLogicalTablePositionOrder = useCallback(
    (activeTable, sourceItems, extraEntry = null) => {
      if (!activeTable) return [];

      const cascadeAxis = getSectorCascadeAxis(activeTable);

      /*
       * Ha nincs vágási irány, biztonságos fallback:
       * y, majd x szerinti sorrend.
       */
      if (!cascadeAxis) {
        const fallbackEntries = (
          Array.isArray(sourceItems) ? sourceItems : []
        ).flatMap((item) =>
          (item.processedPositions || [])
            .filter(
              (position) => String(position.id) === String(activeTable.id)
            )
            .map((position) => ({
              itemId: item.itemId,
              instanceId: position.instanceId,
              position,
              isNew: false
            }))
        );

        if (extraEntry) {
          fallbackEntries.push({
            ...extraEntry,
            isNew: true
          });
        }

        return fallbackEntries.sort((a, b) => {
          const yDifference =
            Number(a.position.y || 0) - Number(b.position.y || 0);

          if (Math.abs(yDifference) > 0.001) {
            return yDifference;
          }

          return Number(a.position.x || 0) - Number(b.position.x || 0);
        });
      }

      const crossAxis = cascadeAxis === "x" ? "y" : "x";
      const tolerance = Math.max(KERF * 2, 2);

      const activeTrim = Number(activeTable.trim ?? activeTable.TRIM ?? 10);

      let tableSize;

      try {
        tableSize = JSON.parse(activeTable.size || "[0,0]");
      } catch {
        tableSize = [0, 0];
      }

      const [tableH, tableW] = tableSize;

      const cascadeLimit = cascadeAxis === "x" ? tableW : tableH;

      const crossLimit = crossAxis === "x" ? tableW : tableH;

      const getStart = (entry, axis) => Number(entry.position?.[axis]) || 0;

      const getSize = (entry, axis) =>
        Number(entry.position?.[axis === "x" ? "width" : "height"]) || 0;

      const getEnd = (entry, axis) =>
        getStart(entry, axis) + getSize(entry, axis);

      const getCenter = (entry, axis) =>
        getStart(entry, axis) + getSize(entry, axis) / 2;

      const entries = (Array.isArray(sourceItems) ? sourceItems : []).flatMap(
        (item) =>
          (item.processedPositions || [])
            .filter(
              (position) => String(position.id) === String(activeTable.id)
            )
            .map((position) => ({
              itemId: item.itemId,
              instanceId: position.instanceId,
              position,
              isNew: false
            }))
      );

      if (extraEntry) {
        entries.push({
          ...extraEntry,
          isNew: true
        });
      }

      const { horizontals, verticals } = parseRigheAbs(activeTable);

      const sectorBoundaryLines =
        crossAxis === "x"
          ? verticals
              .filter(
                (line) =>
                  Number(line.y1) <= activeTrim + tolerance &&
                  Number(line.y2) >= cascadeLimit - activeTrim - tolerance
              )
              .map((line) => Number(line.x))
          : horizontals
              .filter(
                (line) =>
                  Number(line.x1) <= activeTrim + tolerance &&
                  Number(line.x2) >= cascadeLimit - activeTrim - tolerance
              )
              .map((line) => Number(line.y));

      const boundaries = [
        activeTrim,
        ...sectorBoundaryLines,
        crossLimit - activeTrim
      ]
        .filter(Number.isFinite)
        .sort((a, b) => a - b)
        .filter(
          (value, index, array) =>
            index === 0 || Math.abs(value - array[index - 1]) > tolerance
        );

      const sectors = [];

      for (let index = 0; index < boundaries.length - 1; index++) {
        sectors.push({
          start: boundaries[index],
          end: boundaries[index + 1],
          entries: []
        });
      }

      /*
       * Ha a RigheAbs nem adott használható szektorokat,
       * egyetlen teljes szektort használunk.
       */
      if (sectors.length === 0) {
        sectors.push({
          start: activeTrim,
          end: crossLimit - activeTrim,
          entries: []
        });
      }

      entries.forEach((entry) => {
        const center = getCenter(entry, crossAxis);

        const matchingSector =
          sectors.find(
            (sector) =>
              center >= sector.start - tolerance &&
              center <= sector.end + tolerance
          ) ?? sectors[sectors.length - 1];

        matchingSector.entries.push(entry);
      });

      return sectors.flatMap((sector) => {
        const lanes = [];

        const sectorEntries = [...sector.entries].sort(
          (a, b) => getStart(a, crossAxis) - getStart(b, crossAxis)
        );

        sectorEntries.forEach((entry) => {
          const entryStart = getStart(entry, crossAxis);
          const entryEnd = getEnd(entry, crossAxis);
          const entrySize = entryEnd - entryStart;

          const matchingLane = lanes.find((lane) => {
            const overlap =
              Math.min(lane.end, entryEnd) - Math.max(lane.start, entryStart);

            const requiredOverlap =
              Math.min(lane.end - lane.start, entrySize) * 0.7;

            return overlap > 0 && overlap + tolerance >= requiredOverlap;
          });

          if (matchingLane) {
            matchingLane.entries.push(entry);
            matchingLane.start = Math.min(matchingLane.start, entryStart);
            matchingLane.end = Math.max(matchingLane.end, entryEnd);
          } else {
            lanes.push({
              start: entryStart,
              end: entryEnd,
              entries: [entry]
            });
          }
        });

        lanes.sort((a, b) => a.start - b.start);

        return lanes.flatMap((lane, laneIndex) => {
          const laneEntries = [...lane.entries];

          const forward = laneIndex % 2 === 0;

          laneEntries.sort((a, b) => {
            if (forward) {
              return getStart(a, cascadeAxis) - getStart(b, cascadeAxis);
            }

            return getEnd(b, cascadeAxis) - getEnd(a, cascadeAxis);
          });

          return laneEntries;
        });
      });
    },
    [getSectorCascadeAxis, KERF]
  );
  const appendPositionToDati = useCallback(
    ({
      dati,
      item,
      position,
      placedWidth,
      placedHeight,
      tableColor,
      work,
      insertRowNumber
    }) => {
      const rawDati = String(dati ?? "");
      const newline = rawDati.includes("\r\n") ? "\r\n" : "\n";
      const lines = rawDati ? rawDati.split(/\r?\n/) : [];

      const buildDatiPayload = () => {
        const kant = parseManualPlacementKant(item?.kant);

        let sKantString = "0-0";
        let sFlag = "";

        let gKantString = "0-0";
        let gFlag = "";

        if (kant.type === "04") {
          sKantString = `${kant.longCount}-${kant.shortCount}`;
          sFlag = "S";
        }

        if (kant.type === "2") {
          gKantString = `${kant.longCount}-${kant.shortCount}`;
          gFlag = "G";
        }

        const colorName =
          tableColor?.name ?? table?.color?.name ?? item?.color?.name ?? "";

        const colorDescription = colorName;

        const details = item?.details ?? "";

        const itemIdentifier =
          item?.mainid ?? item?.localid ?? item?.itemId ?? "";

        const millingId =
          item?.milling?.millingId ??
          item?.millingId ??
          position?.millingId ??
          "-";

        const username =
          work?.username ??
          work?.user?.name ??
          selectedWork?.username ??
          selectedWork?.user?.name ??
          "";

        const workName = `${username} ${colorName} M:${millingId}`.trim();

        /*
         * A manuális pozíció width/height értéke már a kész
         * alkatrészméret, ezért itt NEM vonunk le újra KERF-et.
         */
        const datiHeight = Number(placedHeight) || 0;
        const datiWidth = Number(placedWidth) || 0;

        return (
          `,,1,,` +
          `${colorName},` +
          `${colorDescription},` +
          `${datiHeight.toFixed(1)},` +
          `${datiWidth.toFixed(1)},` +
          `${sKantString},` +
          `${gKantString},` +
          `${sFlag},` +
          `${gFlag},` +
          `${itemIdentifier},` +
          `${details},` +
          `${workName},,,,,,,,`
        );
      };

      /*
       * Egy teljesen üres Dati esetén létrehozzuk a szekciót.
       */
      if (lines.length === 0) {
        const datiRowNumber = 1;

        return {
          dati: [
            "[Dati]",
            "NumeroDati=1",
            `${datiRowNumber}=${buildDatiPayload()}`
          ].join(newline),
          datiRowNumber,
          count: 1
        };
      }

      const datiSectionIndex = lines.findIndex(
        (line) => line.trim().toLowerCase() === "[dati]"
      );

      if (datiSectionIndex < 0) {
        throw new Error("A [Dati] szekció nem található.");
      }

      let datiSectionEnd = lines.findIndex(
        (line, index) => index > datiSectionIndex && /^\s*\[.+\]\s*$/.test(line)
      );

      if (datiSectionEnd < 0) {
        datiSectionEnd = lines.length;
      }

      const numeroDatiIndex = lines.findIndex(
        (line, index) =>
          index > datiSectionIndex &&
          index < datiSectionEnd &&
          /^\s*NumeroDati\s*=/i.test(line)
      );

      if (numeroDatiIndex < 0) {
        throw new Error("A NumeroDati sor nem található.");
      }

      const existingRows = [];

      for (
        let lineIndex = numeroDatiIndex + 1;
        lineIndex < datiSectionEnd;
        lineIndex++
      ) {
        const match = lines[lineIndex].match(/^\s*(\d+)\s*=(.*)$/);

        if (!match) continue;

        existingRows.push({
          lineIndex,
          rowNumber: Number(match[1])
        });
      }

      const count = existingRows.length + 1;

      const requestedRowNumber = Number(insertRowNumber);

      const datiRowNumber =
        Number.isInteger(requestedRowNumber) &&
        requestedRowNumber >= 1 &&
        requestedRowNumber <= count
          ? requestedRowNumber
          : count;

      /*
       * Régi Dati-sorszám → új Dati-sorszám.
       *
       * Ha a 7. helyre szúrunk:
       * 1..6 változatlan,
       * 7 → 8,
       * 8 → 9,
       * ...
       */
      const oldToNewRowNumbers = new Map();

      existingRows.forEach((row) => {
        oldToNewRowNumbers.set(
          row.rowNumber,
          row.rowNumber >= datiRowNumber ? row.rowNumber + 1 : row.rowNumber
        );
      });

      lines[numeroDatiIndex] = `NumeroDati=${count}`;

      const rowByLineIndex = new Map(
        existingRows.map((row) => [row.lineIndex, row])
      );

      const outputLines = [];

      lines.forEach((line, lineIndex) => {
        if (lineIndex === numeroDatiIndex) {
          outputLines.push(`NumeroDati=${count}`);
          return;
        }

        const existingRow = rowByLineIndex.get(lineIndex);

        /*
         * Az új sort közvetlenül a régi, azonos sorszámú
         * sor elé tesszük.
         */
        if (existingRow && existingRow.rowNumber === datiRowNumber) {
          outputLines.push(`${datiRowNumber}=${buildDatiPayload()}`);
        }

        if (existingRow) {
          const newRowNumber = oldToNewRowNumbers.get(existingRow.rowNumber);

          outputLines.push(
            `${newRowNumber}=${line.replace(/^\s*\d+\s*=/, "")}`
          );

          return;
        }

        outputLines.push(line);
      });

      /*
       * Ha a végére kerül, még nem szúrtuk be a ciklusban.
       */
      if (datiRowNumber === count) {
        const lastOldRow = existingRows[existingRows.length - 1];

        if (lastOldRow) {
          const lastOutputIndex = outputLines.findIndex((line) =>
            line.startsWith(`${oldToNewRowNumbers.get(lastOldRow.rowNumber)}=`)
          );

          outputLines.splice(
            lastOutputIndex + 1,
            0,
            `${datiRowNumber}=${buildDatiPayload()}`
          );
        } else {
          outputLines.splice(
            numeroDatiIndex + 1,
            0,
            `${datiRowNumber}=${buildDatiPayload()}`
          );
        }
      }

      return {
        dati: outputLines.join(newline),
        datiRowNumber,
        count,
        oldToNewRowNumbers
      };
    },
    [selectedWork, table]
  );

  const handlePlaceItemInWaste = useCallback(
    async (waste) => {
      if (placementReplayLockRef.current) {
        return;
      }

      if (placementItemId == null || !isEditing || cannotEdit || !table) {
        return;
      }
      const sourceItems = Array.isArray(latestItemsRef.current)
        ? latestItemsRef.current
        : [];

      const selectedItem = sourceItems.find(
        (item) => String(item.itemId) === String(placementItemId)
      );

      if (!selectedItem) {
        clearPlacementSelection();
        showMessage("error", t("selectedItemMissing"));
        return;
      }

      const validTableIds = new Set(
        (Array.isArray(latestTablesRef.current)
          ? latestTablesRef.current
          : []
        ).map((currentTable) => String(currentTable.id))
      );

      const currentPositions = Array.isArray(selectedItem.processedPositions)
        ? selectedItem.processedPositions
        : [];

      const validPositionCount = currentPositions.filter((position) =>
        validTableIds.has(String(position.id))
      ).length;

      const requestedQty = Math.max(0, Number(selectedItem.qty ?? 0));

      if (requestedQty <= 0) {
        clearPlacementSelection();
        showMessage("error", "Az elem mennyisége nem nagyobb nullánál.");
        return;
      }

      if (validPositionCount >= requestedQty) {
        clearPlacementSelection();
        showMessage("info", "Az elem minden példánya már táblán van.");
        return;
      }

      let size;

      try {
        size = JSON.parse(selectedItem.size || "[0,0]");
      } catch {
        size = [0, 0];
      }

      const originalHeight = Number(size?.[0]);
      const originalWidth = Number(size?.[1]);

      if (!(originalWidth > 0) || !(originalHeight > 0)) {
        showMessage(
          "error",
          "Az elem mérete érvénytelen, ezért nem helyezhető el."
        );
        return;
      }

      const tableColorId = table?.color?.colorId ?? null;
      const itemColorId =
        selectedItem?.color?.colorId ?? selectedItem?.colorId ?? null;

      if (tableColorId == null) {
        showMessage("error", "A kiválasztott táblának nincs színe.");
        return;
      }

      if (itemColorId != null && String(itemColorId) !== String(tableColorId)) {
        showMessage(
          "error",
          "Az elem színe nem egyezik a kiválasztott tábla színével."
        );
        return;
      }

      const rotation = Number(placementRotation) === 1 ? 1 : 0;
      const wantsRotated = rotation === 1;

      const itemCanRotate =
        selectedItem.rotable !== false && selectedItem.rotatable !== false;

      if (wantsRotated && !itemCanRotate && !placementForceRotate) {
        showMessage(
          "error",
          "Ez az elem nem forgatható. Válaszd újra a függőleges elhelyezést és erősítsd meg."
        );
        return;
      }

      const placedWidth = wantsRotated ? originalHeight : originalWidth;
      const placedHeight = wantsRotated ? originalWidth : originalHeight;

      const tolerance = 0.001;

      const fitsSelectedOrientation =
        placedWidth <= Number(waste.width) + tolerance &&
        placedHeight <= Number(waste.height) + tolerance;

      if (!fitsSelectedOrientation) {
        showMessage(
          "error",
          wantsRotated
            ? "A kiválasztott elem függőlegesen nem fér el ebben a waste területben."
            : "A kiválasztott elem vízszintesen nem fér el ebben a waste területben."
        );
        return;
      }
      const newPosition = {
        instanceId: getNextManualInstanceId(selectedItem),
        x: Number(waste.x),
        y: Number(waste.y),
        rotation,
        id: table.id,
        width: placedWidth,
        height: placedHeight,
        datiRowNumber: null
      };

      /*
       * Ezeket a try blokkon kívül hozzuk létre,
       * mert később a state-frissítésnél és a logolásnál is használjuk.
       */
      const sourceTables = Array.isArray(latestTablesRef.current)
        ? latestTablesRef.current
        : [];

      const currentWorkingTable =
        sourceTables.find(
          (currentTable) => String(currentTable.id) === String(table.id)
        ) ?? table;

      const currentWorkingColor =
        colorsFromStore.find(
          (color) =>
            String(color.colorId) ===
            String(currentWorkingTable?.color?.colorId)
        ) ??
        currentWorkingTable?.color ??
        null;

      let datiResult;
      const logicalOrder = getLogicalTablePositionOrder(
        currentWorkingTable,
        sourceItems,
        {
          itemId: selectedItem.itemId,
          instanceId: newPosition.instanceId,
          position: newPosition
        }
      );

      const newLogicalIndex = logicalOrder.findIndex(
        (entry) =>
          entry.isNew === true &&
          String(entry.itemId) === String(selectedItem.itemId) &&
          String(entry.instanceId) === String(newPosition.instanceId)
      );

      if (newLogicalIndex < 0) {
        showMessage(
          "error",
          "Nem sikerült meghatározni az új elem Dati-sorrendjét."
        );

        return;
      }

      /*
       * A tömb indexe 0-tól, a Dati sorszám 1-től indul.
       */
      const insertDatiRowNumber = newLogicalIndex + 1;
      try {
        datiResult = appendPositionToDati({
          dati: currentWorkingTable.dati ?? "",
          item: selectedItem,
          position: newPosition,
          placedWidth,
          placedHeight,
          tableColor: currentWorkingColor,
          work: selectedWork,
          insertRowNumber: insertDatiRowNumber
        });
      } catch (error) {
        console.error("Nem sikerült az új Dati-sort létrehozni:", error);

        showMessage("error", t("datiWriteFailed"));

        return;
      }

      /*
       * A Dati beszúrás után már ismert a stabil sorszám.
       */
      newPosition.datiRowNumber = datiResult.datiRowNumber;

      const shiftedItems = sourceItems.map((item) => {
        let changed = false;

        const shiftedPositions = (item.processedPositions || []).map(
          (position) => {
            if (String(position.id) !== String(currentWorkingTable.id)) {
              return position;
            }

            const oldRowNumber = Number(position.datiRowNumber);

            const newRowNumber =
              datiResult.oldToNewRowNumbers.get(oldRowNumber);

            if (
              !Number.isInteger(newRowNumber) ||
              newRowNumber === oldRowNumber
            ) {
              return position;
            }

            changed = true;

            return {
              ...position,
              datiRowNumber: newRowNumber
            };
          }
        );

        if (!changed) {
          return item;
        }

        return {
          ...item,
          processedPositions: shiftedPositions,
          tablePosition: serializeProcessedPositions(shiftedPositions)
        };
      });

      const nextItems = shiftedItems.map((item) => {
        if (String(item.itemId) !== String(selectedItem.itemId)) {
          return item;
        }

        const existingShiftedPositions = item.processedPositions || [];

        const finalPositions = [...existingShiftedPositions, newPosition];

        return {
          ...item,

          ...(wantsRotated && !itemCanRotate && placementForceRotate
            ? {
                rotable: true,
                rotatable: true
              }
            : {}),

          color: {
            colorId: itemColorId ?? tableColorId
          },

          processedPositions: finalPositions,
          tablePosition: serializeProcessedPositions(finalPositions)
        };
      });

      /*
       * Ha a sourceTables valamiért üres, az aktuális tábla
       * akkor is bekerül a frissítendő tömbbe.
       */
      const tablesToUpdate =
        sourceTables.length > 0 ? sourceTables : [currentWorkingTable];

      const nextTables = tablesToUpdate.map((currentTable) => {
        if (String(currentTable.id) !== String(table.id)) {
          return currentTable;
        }

        return {
          ...currentTable,
          dati: datiResult.dati,
          partCount: datiResult.count,
          measureCount: datiResult.count
        };
      });

      const selectedItemAfterInsert = nextItems.find(
        (item) => String(item.itemId) === String(selectedItem.itemId)
      );

      const nextValidTableIds = new Set(
        nextTables.map((currentTable) => String(currentTable.id))
      );

      const placedCountAfterInsert = (
        selectedItemAfterInsert?.processedPositions || []
      ).filter((position) => nextValidTableIds.has(String(position.id))).length;

      const itemQtyAfterInsert = Math.max(
        0,
        Number(selectedItemAfterInsert?.qty ?? requestedQty)
      );

      const remainingCountAfterInsert = Math.max(
        0,
        itemQtyAfterInsert - placedCountAfterInsert
      );

      const shouldKeepPlacementMode = remainingCountAfterInsert > 0;

      console.groupCollapsed(
        `[Manuális elhelyezés] Új Dati – tábla: ${table.id}`
      );

      console.log("Elhelyezett item:", {
        itemId: selectedItem.itemId,
        instanceId: newPosition.instanceId,
        tableId: newPosition.id,
        x: newPosition.x,
        y: newPosition.y,
        rotation: newPosition.rotation,
        datiRowNumber: newPosition.datiRowNumber
      });

      console.log("Dati ELŐTTE:", currentWorkingTable.dati ?? "");
      console.log("Dati UTÁNA:", datiResult.dati);

      console.groupEnd();

      latestItemsRef.current = nextItems;
      latestTablesRef.current = nextTables;

      placementReplayLockRef.current = true;

      try {
        if (typeof onReplaySingleTable !== "function") {
          throw new Error("A single-table replay callback nincs bekötve.");
        }

        const replayResult = await onReplaySingleTable({
          tableId: table.id,
          items: nextItems,
          tables: nextTables
        });

        latestItemsRef.current = replayResult?.items ?? nextItems;
        latestTablesRef.current = replayResult?.tables ?? nextTables;

        onDirty(true);

        if (shouldKeepPlacementMode) {
          showMessage(
            "success",
            t("itemPlacedVerticalRemaining", {
              count: remainingCountAfterInsert
            })
          );
        } else {
          clearPlacementSelection();

          showMessage(
            "success",
            wantsRotated
              ? "Az elem összes példánya függőlegesen elhelyezve."
              : "Az elem összes példánya vízszintesen elhelyezve."
          );
        }
      } catch (error) {
        console.error("A célzott tábla replay sikertelen:", error);

        setEditedItems(nextItems);
        setEditedTables(nextTables);

        latestItemsRef.current = nextItems;
        latestTablesRef.current = nextTables;

        onDirty(true);

        if (!shouldKeepPlacementMode) {
          clearPlacementSelection();
        }

        showMessage(
          "error",
          shouldKeepPlacementMode
            ? `Az elem elhelyezve, de a replay nem sikerült. Még ${remainingCountAfterInsert} db elhelyezhető.`
            : "Az elem elhelyezve, de az érintett tábla replaye nem sikerült."
        );
      } finally {
        placementReplayLockRef.current = false;
      }
    },
    [
      placementItemId,
      placementRotation,
      placementForceRotate,
      isEditing,
      cannotEdit,
      table,
      getNextManualInstanceId,
      appendPositionToDati,
      serializeProcessedPositions,
      setEditedItems,
      setEditedTables,
      colorsFromStore,
      selectedWork,
      onDirty,
      clearPlacementSelection,
      showMessage,
      onReplaySingleTable,
      getLogicalTablePositionOrder
    ]
  );

  const toggleItemSelection = useCallback((itemId, instanceId) => {
    setSelectedItemPositions((previousSelections) => {
      const isAlreadySelected = previousSelections.some(
        (selection) =>
          selection.itemId === itemId && selection.instanceId === instanceId
      );

      if (isAlreadySelected) {
        return previousSelections.filter(
          (selection) =>
            !(
              selection.itemId === itemId && selection.instanceId === instanceId
            )
        );
      }

      return [
        ...previousSelections,
        {
          itemId,
          instanceId
        }
      ];
    });
  }, []);
  const addItemToSelection = useCallback((itemId, instanceId) => {
    setSelectedItemPositions((previousSelections) => {
      const alreadySelected = previousSelections.some(
        (selection) =>
          selection.itemId === itemId && selection.instanceId === instanceId
      );

      if (alreadySelected) {
        return previousSelections;
      }

      return [
        ...previousSelections,
        {
          itemId,
          instanceId
        }
      ];
    });
  }, []);

  const removeItemFromSelection = useCallback((itemId, instanceId) => {
    setSelectedItemPositions((previousSelections) =>
      previousSelections.filter(
        (selection) =>
          !(selection.itemId === itemId && selection.instanceId === instanceId)
      )
    );
  }, []);

  const expandRemovalToSectorRemainder = useCallback(
    (positions, sourceItems, activeTable) =>
      expandSectorRemoval(
        positions,
        sourceItems,
        activeTable,
        getSectorCascadeAxis(activeTable),
        KERF
      ),
    [getSectorCascadeAxis, KERF]
  );

  const removeItemPositions = useCallback(
    async (positionsToRemove) => {
      if (
        cannotEdit ||
        placementReplayLockRef.current ||
        !Array.isArray(positionsToRemove) ||
        positionsToRemove.length === 0
      )
        return;

      const sourceItems = latestItemsRef.current ?? [];
      const sourceTables = latestTablesRef.current ?? [];
      const workingTable =
        sourceTables.find(
          (candidate) => String(candidate.id) === String(table?.id)
        ) ?? table;
      if (!workingTable) return;

      const expanded = expandRemovalToSectorRemainder(
        positionsToRemove,
        sourceItems,
        workingTable
      );
      const removalKeys = new Set(
        expanded.map(
          (selection) =>
            `${String(selection.itemId)}::${String(selection.instanceId)}`
        )
      );
      const updatedItems = sourceItems.map((item) => {
        const positions = item.processedPositions || [];
        const remaining = positions.filter(
          (position) =>
            String(position.id) !== String(workingTable.id) ||
            !removalKeys.has(
              `${String(item.itemId)}::${String(position.instanceId)}`
            )
        );
        if (remaining.length === positions.length) return item;
        return {
          ...item,
          processedPositions: remaining,
          tablePosition: serializeProcessedPositions(remaining)
        };
      });

      placementReplayLockRef.current = true;
      setIsLoading(true);
      try {
        if (typeof onReplaySingleTable !== "function") {
          throw new Error("A single-table replay callback nincs bekötve.");
        }
        const result = await onReplaySingleTable({
          tableId: workingTable.id,
          items: updatedItems,
          tables: sourceTables.length ? sourceTables : [workingTable]
        });
        latestItemsRef.current = result.items;
        latestTablesRef.current = result.tables;
        setEditedItems(result.items);
        setProcessedItems(result.items);
        setCreatedItems(result.items);
        setEditedTables(result.tables);
        setSelectedItemPositions([]);
        setSelectedItemPosition?.(null);
        onDirty(true);
      } catch (error) {
        console.error("A törlés utáni újravágás sikertelen:", error);
        showMessage(
          "error",
          "Nem sikerült újragenerálni a vágásokat. A törlés nem történt meg."
        );
      } finally {
        placementReplayLockRef.current = false;
        setIsLoading(false);
      }
    },
    [
      cannotEdit,
      table,
      expandRemovalToSectorRemainder,
      serializeProcessedPositions,
      onReplaySingleTable,
      setIsLoading,
      setEditedItems,
      setProcessedItems,
      setCreatedItems,
      setEditedTables,
      setSelectedItemPosition,
      onDirty,
      showMessage
    ]
  );

  const handleRemoveItemPosition = useCallback(
    (event, itemId, instanceId) => {
      event.preventDefault();
      event.stopPropagation();

      const clickedItemIsSelected = selectedItemPositions.some(
        (selection) =>
          selection.itemId === itemId && selection.instanceId === instanceId
      );

      const positionsToRemove =
        clickedItemIsSelected && selectedItemPositions.length > 0
          ? selectedItemPositions
          : [{ itemId, instanceId }];

      removeItemPositions(positionsToRemove);
    },
    [selectedItemPositions, removeItemPositions]
  );

  useEffect(() => {
    const handleSelectionKey = (event) => {
      if (event.key !== "Escape" && event.key !== "Delete") return;

      const target = event.target;
      const targetTagName = target?.tagName?.toUpperCase();

      const userIsTyping =
        target?.isContentEditable ||
        targetTagName === "INPUT" ||
        targetTagName === "TEXTAREA" ||
        targetTagName === "SELECT";

      if (userIsTyping) return;

      if (event.key === "Escape") {
        if (selectedItemPositions.length === 0) return;

        event.preventDefault();

        setSelectedItemPositions([]);
        setSelectedItemPosition?.(null);

        shiftSelectingRef.current = false;
        shiftSelectionModeRef.current = null;
        suppressNextClickRef.current = false;

        return;
      }

      if (cannotEdit) return;
      if (selectedItemPositions.length === 0) return;

      event.preventDefault();

      removeItemPositions(selectedItemPositions);
    };

    window.addEventListener("keydown", handleSelectionKey);

    return () => {
      window.removeEventListener("keydown", handleSelectionKey);
    };
  }, [
    cannotEdit,
    selectedItemPositions,
    removeItemPositions,
    setSelectedItemPosition
  ]);
  useEffect(() => {
    const stopShiftSelection = () => {
      shiftSelectingRef.current = false;
      shiftSelectionModeRef.current = null;

      window.setTimeout(() => {
        suppressNextClickRef.current = false;
      }, 0);
    };

    window.addEventListener("mouseup", stopShiftSelection);
    window.addEventListener("blur", stopShiftSelection);

    return () => {
      window.removeEventListener("mouseup", stopShiftSelection);
      window.removeEventListener("blur", stopShiftSelection);
    };
  }, []);

  const backgroundImageStyle = useMemo(() => {
    if (!bgImageBase64) return "none";

    const raw = String(bgImageBase64).trim();

    // Ha már teljes data URL-ként van mentve
    if (raw.startsWith("data:image/")) {
      return `url("${raw}")`;
    }

    // Ha URL/blob formában van
    if (raw.startsWith("http") || raw.startsWith("blob:")) {
      return `url("${raw}")`;
    }

    // MIME tippelés base64 eleje alapján
    const mime = raw.startsWith("iVBOR")
      ? "image/png"
      : raw.startsWith("UklGR")
        ? "image/webp"
        : "image/jpeg";

    return `url("data:${mime};base64,${raw}")`;
  }, [bgImageBase64]);

  const [height1, width1] = tableSize;
  const displayTrim = table?.split === true ? 0 : TRIM;
  const height = height1 + 2 * displayTrim;
  const width = width1 + 2 * displayTrim;
  const memoizedDiagonalLines = useMemo(
    () => generateDiagonalLines(width * scaleFactor, height * scaleFactor),
    [width, height, scaleFactor]
  );
  const openAddTableModal = (size, isHalfTable = false) => {
    const normalizedSize = normalizeTableDimension(size);

    if (!normalizedSize) {
      showMessage("error", "Az új tábla mérete érvénytelen.");
      return;
    }

    const inheritedTrim = Number(
      table?.trim ?? table?.TRIM ?? DEFAULT_NEW_TABLE_TRIM
    );

    setAddTableModal({ show: true, size: normalizedSize, isHalfTable });
    setNewTableTrim(
      Number.isFinite(inheritedTrim) && inheritedTrim >= 0
        ? inheritedTrim
        : DEFAULT_NEW_TABLE_TRIM
    );
    setNewTableCutOrientation(
      normalizeCutOrientationValue(
        table?.cutOrientation ?? table?.cut_orientation ?? false
      )
    );
  };

  const closeAddTableModal = () => {
    setAddTableModal({ show: false, size: null, isHalfTable: false });
  };

  const openEmptyAddTableModal = () => {
    const firstColor =
      (colorsFromStore || []).find(
        (color) =>
          String(color.colorId) === String(firstItemColorId) &&
          normalizeTableDimension(color.dimension)
      ) ||
      (colorsFromStore || []).find((color) =>
        normalizeTableDimension(color.dimension)
      );

    if (!firstColor) {
      showMessage("error", "Nincs használható táblaanyag-méret.");
      return;
    }

    setSelectedColorId(firstColor.colorId);
    openAddTableModal(firstColor.dimension, false);
  };

  const createTableForCurrentColor = (
    size,
    isHalfTable = false,
    options = {}
  ) => {
    if (!currentTableColor || currentTableColorId == null) {
      showMessage("error", "Az aktuális tábla színe nem található.");
      return;
    }

    const resolvedTrim = Number(options.trim ?? DEFAULT_NEW_TABLE_TRIM);
    if (!Number.isFinite(resolvedTrim) || resolvedTrim < 0) {
      showMessage("error", "A trim értéke érvénytelen.");
      return;
    }

    const normalizedSize = shrinkTableDimension(size, 2 * resolvedTrim);
    if (!normalizedSize) {
      showMessage("error", "Az új tábla mérete érvénytelen.");
      return;
    }

    const resolvedCutOrientation = normalizeCutOrientationValue(
      options.cutOrientation ?? false
    );
    const sourceTables =
      isEditing && Array.isArray(latestTablesRef.current)
        ? latestTablesRef.current
        : Array.isArray(createdTables)
          ? createdTables
          : [];
    const sourceItems =
      isEditing && Array.isArray(latestItemsRef.current)
        ? latestItemsRef.current
        : Array.isArray(processedItems) && processedItems.length > 0
          ? processedItems
          : Array.isArray(items)
            ? items
            : [];
    const nextItems = structuredClone(sourceItems);
    const smallestTemporaryId = sourceTables.reduce(
      (smallestId, currentTable) => {
        const currentId = Number(currentTable?.id);
        return Number.isFinite(currentId)
          ? Math.min(smallestId, currentId)
          : smallestId;
      },
      0
    );
    const temporaryId = smallestTemporaryId < 0 ? smallestTemporaryId - 1 : -1;
    const newTable = {
      ...(table ?? {}),
      id: temporaryId,
      tableIndex: temporaryId,
      frontend: true,
      work: { workId: Number(workId) },
      color: { ...currentTableColor, colorId: currentTableColorId },
      price: currentTableColor.price,
      size: normalizedSize,
      trim: resolvedTrim,
      TRIM: resolvedTrim,
      dati: "",
      riferimenti: "",
      righe: "",
      righeAbs: "",
      righe_abs: "",
      cutOrientation: resolvedCutOrientation,
      tableUsage: 0,
      cutLength: 0,
      partCount: 0,
      measureCount: 0,
      kantLength: "",
      isHalfTable
    };
    const nextTables = [...sourceTables, newTable];
    latestTablesRef.current = nextTables;
    latestItemsRef.current = nextItems;
    const nextFilteredTables = nextTables.filter((candidateTable) => {
      const candidateColorId = candidateTable?.color?.colorId;
      return (
        !isMDFColor(candidateColorId) &&
        String(candidateColorId) === String(currentTableColorId)
      );
    });
    const nextTableIndex = nextFilteredTables.findIndex(
      (candidateTable) => String(candidateTable.id) === String(temporaryId)
    );

    setEditedTables(nextTables);
    setEditedItems(nextItems);
    setProcessedItems(nextItems);
    setIsEditing(true);
    setSelectedColorId(currentTableColorId);
    setCurrentTableIndex(nextTableIndex >= 0 ? nextTableIndex : 0);
    setShowNoTableText(false);
    onDirty(true);
    showMessage(
      "success",
      isHalfTable ? "A fél tábla hozzáadva." : "A teljes tábla hozzáadva."
    );
  };

  const handleConfirmAddTable = () => {
    if (!addTableModal.size) {
      showMessage("error", "Hiányzik az új tábla mérete.");
      return;
    }

    const trim = Number(newTableTrim);
    if (!Number.isFinite(trim) || trim < 0) {
      showMessage("error", "A trim értéke érvénytelen.");
      return;
    }

    createTableForCurrentColor(addTableModal.size, addTableModal.isHalfTable, {
      trim,
      cutOrientation: newTableCutOrientation
    });
    closeAddTableModal();
  };

  if (!createdTables || createdTables.length === 0) {
    return (
      <>
        <div className="table-viewer-component__empty-state">
          <p>{t("noTablesYet")}</p>
          {!cannotEdit && hasDirty && (
            <button
              onClick={async () => {
                try {
                  setIsLoading(true);
                  await saveDirtyItemsIfAny(false);
                  onDirty(false);
                } catch (e) {
                  console.error(e);
                  alert(t("saveFailed"));
                } finally {
                  setIsLoading(false);
                }
              }}
              className="table-viewer-component__empty-action table-viewer-component__empty-action--save"
              disabled={!hasDirty}
            >
              {t("save")}
            </button>
          )}
          {!cannotEdit && (
            <button
              onClick={openEmptyAddTableModal}
              className={
                hasDirty
                  ? "table-viewer-component__empty-action table-viewer-component__empty-action--add table-viewer-component__empty-action--add-offset"
                  : "table-viewer-component__empty-action table-viewer-component__empty-action--add"
              }
            >
              {t("addTable")}
            </button>
          )}
          <button
            onClick={onGenerate}
            className={
              hasDirty
                ? "table-viewer-component__empty-action table-viewer-component__empty-action--generate table-viewer-component__empty-action--generate-offset"
                : "table-viewer-component__empty-action table-viewer-component__empty-action--generate"
            }
          >
            {t("generateBtn")}
          </button>
        </div>
        <Modal show={addTableModal.show} onHide={closeAddTableModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>{t("newTableSettings")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div style={{ display: "grid", gap: "16px" }}>
              <Form.Group>
                <Form.Label>{t("color")}</Form.Label>
                <Form.Select
                  value={currentTableColorId ?? ""}
                  onChange={(event) => {
                    const nextColor = colorsFromStore.find(
                      (color) => String(color.colorId) === event.target.value
                    );
                    if (!nextColor) return;
                    setSelectedColorId(nextColor.colorId);
                    openAddTableModal(nextColor.dimension, false);
                  }}
                >
                  {(colorsFromStore || []).map((color) => (
                    <option key={color.colorId} value={color.colorId}>
                      {t(color.name) || color.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <div>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  <Button
                    variant={
                      !addTableModal.isHalfTable ? "primary" : "outline-primary"
                    }
                    onClick={() =>
                      openAddTableModal(currentFullDimension, false)
                    }
                    disabled={!currentFullDimension}
                  >
                    {t("fullTable")}
                  </Button>
                  <Button
                    variant={
                      addTableModal.isHalfTable ? "primary" : "outline-primary"
                    }
                    onClick={() =>
                      openAddTableModal(currentSplitDimension, true)
                    }
                    disabled={!currentSplitDimension}
                  >
                    {t("halfTable")}
                  </Button>
                </div>
                <div style={{ opacity: 0.75, marginTop: "8px" }}>
                  {t("dimension")}: {formatTableDimension(addTableModal.size)}
                </div>
              </div>
              <Form.Group>
                <Form.Label>{t("trim")}</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="1"
                  value={newTableTrim}
                  onChange={(event) => setNewTableTrim(event.target.value)}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>{t("cutOrientation")}</Form.Label>
                <Form.Select
                  value={newTableCutOrientation ? "vertical" : "horizontal"}
                  onChange={(event) =>
                    setNewTableCutOrientation(event.target.value === "vertical")
                  }
                >
                  <option value="horizontal">{t("horizontal")}</option>
                  <option value="vertical">{t("vertical")}</option>
                </Form.Select>
              </Form.Group>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeAddTableModal}>
              {t("cancel")}
            </Button>
            <Button variant="primary" onClick={handleConfirmAddTable}>
              {t("addTable")}
            </Button>
          </Modal.Footer>
        </Modal>
      </>
    );
  }

  if (isLoading) return <Loading />;

  const handleAddTable = () => {
    if (!currentTableColor) {
      showMessage("error", "Az aktuális tábla színe nem található.");
      return;
    }

    if (!currentFullDimension) {
      showMessage("error", "A szín teljes tábla mérete érvénytelen.");
      return;
    }

    openAddTableModal(currentFullDimension, false);
  };

  const handleDeleteTable = () => {
    if (!table) return;

    const confirmDelete = window.confirm(t("deleteTableConfirm"));

    if (!confirmDelete) return;

    const tableIdToDelete = table.id;

    const sourceTables = Array.isArray(latestTablesRef.current)
      ? latestTablesRef.current
      : [];

    const sourceItems = Array.isArray(latestItemsRef.current)
      ? latestItemsRef.current
      : [];

    /*
     * Csak a táblát töröljük.
     */
    const newEditedTables = sourceTables.filter(
      (currentTable) => String(currentTable.id) !== String(tableIdToDelete)
    );

    /*
     * Az itemek megmaradnak.
     * Csak a törölt táblához tartozó pozíciókat távolítjuk el.
     */
    const newEditedItems = sourceItems.map((item) => {
      const currentPositions = Array.isArray(item.processedPositions)
        ? item.processedPositions
        : [];

      const remainingPositions = currentPositions.filter(
        (position) => String(position.id) !== String(tableIdToDelete)
      );

      /*
       * Ha az item nem volt ezen a táblán, változatlanul visszaadjuk.
       */
      if (remainingPositions.length === currentPositions.length) {
        return item;
      }

      return {
        ...item,
        processedPositions: remainingPositions,
        tablePosition: serializeProcessedPositions(remainingPositions)
      };
    });

    /*
     * A refeket azonnal szinkronizáljuk.
     */
    latestTablesRef.current = newEditedTables;
    latestItemsRef.current = newEditedItems;

    /*
     * Csak a szerkesztési állapotot módosítjuk.
     * A tényleges mentést a fő Mentés gomb végzi.
     */
    setEditedTables(newEditedTables);
    setEditedItems(newEditedItems);

    setSelectedItemPosition(null);
    setSelectedItemPositions([]);
    setPlacementItemId(null);

    if (newEditedTables.length > 0) {
      setCurrentTableIndex(0);
      setShowNoTableText(false);
    } else {
      setCurrentTableIndex(-1);
      setShowNoTableText(true);

      /*
       * Fontos: nem kapcsoljuk ki a szerkesztési módot,
       * különben a fő mentés nem az edited state-ekből dolgozna.
       */
    }

    onDirty(true);

    showMessage(
      "success",
      "A tábla törlésre jelölve. Az elemek lekerültek róla, de megmaradtak."
    );
  };

  const renderItems = isEditing
    ? Array.isArray(editedItems)
      ? editedItems
      : []
    : Array.isArray(processedItems)
      ? processedItems
      : Array.isArray(items)
        ? items
        : [];

  const filteredItems = renderItems.filter((item) =>
    (item.processedPositions || []).some((pos) => pos.id === table?.id)
  );

  const getWasteCollapseInfo = (waste) => {
    const positions = filteredItems.flatMap((item) =>
      (item.processedPositions || [])
        .filter((position) => String(position.id) === String(table?.id))
        .map((position) => ({ ...position, itemId: item.itemId }))
    );
    const tolerance = 0.5;
    const configuredAxis = getSectorCascadeAxis(table);
    const axis = configuredAxis === "x" ? "y" : "x";
    const gapStart = axis === "y" ? waste.y : waste.x;
    const gapSize = axis === "y" ? waste.height : waste.width;
    const gapEnd = gapStart + gapSize;

    let tableDimensions;
    try {
      tableDimensions = JSON.parse(table?.size || "[0,0]");
    } catch {
      tableDimensions = [0, 0];
    }

    const [tableHeight, tableWidth] = tableDimensions;
    const crossLimit = axis === "y" ? Number(tableWidth) : Number(tableHeight);
    const crossStart = axis === "y" ? waste.x : waste.y;
    const crossEnd = crossStart + (axis === "y" ? waste.width : waste.height);
    const usableCrossStart = TRIM;
    const usableCrossEnd = crossLimit - TRIM;
    const spansFullUsableLength =
      crossStart <= usableCrossStart + tolerance &&
      crossEnd >= usableCrossEnd - tolerance;

    if (!spansFullUsableLength || gapSize <= tolerance) return null;

    const overlapsWasteLength = (position) => {
      const positionCrossStart = axis === "y" ? position.x : position.y;
      const positionCrossEnd =
        positionCrossStart + (axis === "y" ? position.width : position.height);

      return (
        Math.min(positionCrossEnd, crossEnd) >
        Math.max(positionCrossStart, crossStart) + tolerance
      );
    };
    const hasBefore = positions.some((position) => {
      const end =
        (axis === "y" ? position.y : position.x) +
        (axis === "y" ? position.height : position.width);

      return end <= gapStart + tolerance && overlapsWasteLength(position);
    });

    const hasAfter = positions.some((position) => {
      const start = axis === "y" ? position.y : position.x;

      return start >= gapEnd - tolerance && overlapsWasteLength(position);
    });

    /*
     * A waste akkor is összecsúsztatható, ha az elején
     * nem item szektor van, hanem közvetlenül a trimelt táblaszél.
     *
     * Normál eset:
     * ITEM | WASTE | ITEM
     *
     * Újonnan engedett eset:
     * TRIM | WASTE | ITEM
     */
    const touchesStartTrim = gapStart <= TRIM + tolerance;

    const canCollapse = hasAfter && (hasBefore || touchesStartTrim);

    if (!canCollapse) {
      return null;
    }

    return {
      axis,
      shift: gapSize,
      icon: axis === "y" ? arrowDownOutline : chevronBackOutline,
      startsAtTrim: !hasBefore && touchesStartTrim
    };
  };

  const handleCollapseWaste = async (waste, collapseInfo) => {
    if (!collapseInfo || !table || typeof onReplaySingleTable !== "function") {
      return;
    }

    const sourceItems = Array.isArray(latestItemsRef.current)
      ? latestItemsRef.current
      : [];
    const sourceTables = Array.isArray(latestTablesRef.current)
      ? latestTablesRef.current
      : [];
    const { axis, shift } = collapseInfo;
    const gapStart = axis === "y" ? waste.y : waste.x;
    const gapEnd = gapStart + shift;
    const crossStart = axis === "y" ? waste.x : waste.y;
    const crossEnd = crossStart + (axis === "y" ? waste.width : waste.height);

    const nextItems = sourceItems.map((item) => {
      let changed = false;
      const nextPositions = (item.processedPositions || []).map((position) => {
        if (String(position.id) !== String(table.id)) return position;

        const start = axis === "y" ? position.y : position.x;
        const positionCrossStart = axis === "y" ? position.x : position.y;
        const positionCrossEnd =
          positionCrossStart +
          (axis === "y" ? position.width : position.height);
        const overlapsWaste =
          Math.min(positionCrossEnd, crossEnd) >
          Math.max(positionCrossStart, crossStart) + 0.5;

        if (start < gapEnd - 0.5 || !overlapsWaste) return position;

        changed = true;
        return {
          ...position,
          ...(axis === "y"
            ? { y: position.y - shift }
            : { x: position.x - shift })
        };
      });

      if (!changed) return item;

      return {
        ...item,
        processedPositions: nextPositions,
        tablePosition: serializeProcessedPositions(nextPositions)
      };
    });

    try {
      setIsLoading(true);
      const replayResult = await onReplaySingleTable({
        tableId: table.id,
        items: nextItems,
        tables: sourceTables
      });
      const replayedItems = replayResult?.items ?? nextItems;
      const replayedTables = replayResult?.tables ?? sourceTables;

      latestItemsRef.current = replayedItems;
      latestTablesRef.current = replayedTables;
      setEditedItems(replayedItems);
      setProcessedItems(replayedItems);
      setCreatedItems(replayedItems);
      setEditedTables(replayedTables);
      setSelectedItemPositions([]);
      setSelectedItemPosition?.(null);
      onDirty(true);
      showMessage("success", "A szektorok összecsúsztatva és újravágva.");
    } catch (error) {
      console.error("A szektorok összecsúsztatása sikertelen:", error);
      showMessage("error", "Nem sikerült összecsúsztatni a szektorokat.");
    } finally {
      setIsLoading(false);
    }
  };

  async function handleRotateItem() {
    if (!selectedItemPosition) return;

    const { itemId, instanceId } = selectedItemPosition;
    const item = editedItems.find((i) => i.itemId === itemId);
    if (!item) return;

    const pos = item.processedPositions.find(
      (p) => p.instanceId === instanceId
    );
    if (!pos) return;

    let { x, y, rotation } = pos;

    console.log("rotation: ", rotation);
    const newRot = rotation === 0 ? 1 : 0;
    console.log("newRot: ", newRot);

    const w = pos.height;
    const h = pos.width;

    const [tableH, tableW] = JSON.parse(table.size || "[0,0]");

    const otherPositions = getTablePositions(editedItems, table.id).filter(
      (p) => p.instanceId !== instanceId
    );

    const insideTable =
      x >= TRIM &&
      y >= TRIM &&
      x + w <= tableW - TRIM &&
      y + h <= tableH - TRIM;

    const collides = otherPositions.some(
      (p) =>
        x < p.x + p.width + KERF &&
        x + w + KERF > p.x &&
        y < p.y + p.height + KERF &&
        y + h + KERF > p.y
    );

    let newX = x;
    let newY = y;

    if (!insideTable || collides) {
      // keresünk új szabad pozíciót
      const free = findFreePosition(
        w,
        h,
        instanceId,
        tableW - TRIM * 2,
        tableH - TRIM * 2,
        otherPositions
      );

      if (!free) {
        return alert(t("rotateFailed"));
      }

      newX = free.x + TRIM;
      newY = free.y + TRIM;
    }

    onDirty(true);

    setEditedItems((cur) => {
      const updated = cur.map((it) =>
        it.itemId === itemId
          ? {
              ...it,
              processedPositions: it.processedPositions.map((p) =>
                p.instanceId === instanceId
                  ? {
                      ...p,
                      rotation: newRot,
                      width: w,
                      height: h,
                      x: newX,
                      y: newY
                    }
                  : p
              )
            }
          : it
      );

      return updated;
    });

    // 🔥 FORCE RESELECT (ez a kulcs)
    setSelectedItemPosition(null);
    requestAnimationFrame(() => {
      setSelectedItemPosition({ itemId, instanceId });
    });
  }

  const handlePersistChanges = async () => {
    try {
      setIsLoading(true);
      showMessage("info", t("savingInProgress"));

      const currentItems = isEditing
        ? editedItems
        : Array.isArray(processedItems)
          ? processedItems
          : items;

      const currentTables = isEditing ? editedTables : createdTables;

      const hasNewTables = currentTables.some((table) => Number(table.id) < 0);

      let itemsToSave = currentItems;
      let tablesToSave = currentTables;

      if (hasNewTables) {
        const generatedSaveResult = await saveGeneratedTables(
          currentItems,
          currentTables
        );

        itemsToSave = generatedSaveResult.items;
        tablesToSave = generatedSaveResult.tables;
      }

      const forceGeneratedItemSave = generatedSavePendingRef.current;

      await saveDirtyItemsIfAny(
        false,
        itemsToSave,
        tablesToSave,
        forceGeneratedItemSave
      );

      generatedSavePendingRef.current = false;

      onDirty(false);
    } catch (error) {
      console.error("Saving failed:", error);
      alert(t("saveFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleMoveToTable = (targetTableId) => {
    if (!selectedItemPosition || !isEditing) return;

    const { itemId, instanceId } = selectedItemPosition;
    const selectedItem = editedItems.find((it) => it.itemId == itemId);
    if (!selectedItem) return;

    // processedPositions-ben megkeressük a mozgó elem indexét
    const posIdx = selectedItem.processedPositions.findIndex(
      (p) => p.instanceId === instanceId
    );
    if (posIdx < 0) return;

    // ebből a pozícióból olvassuk ki a koordinátákat
    const pos = selectedItem.processedPositions.find(
      (p) => p.instanceId === instanceId
    );
    if (!pos) return;
    const { rotation } = pos;
    const [origW, origH] = JSON.parse(selectedItem.size || "[0,0]");
    console.log(
      "origW, origH: ",
      origW,
      origH,
      " selectedItem.size:",
      selectedItem.size
    );
    const itemWidth = rotation === 1 ? origH : origW;
    const itemHeight = rotation === 1 ? origW : origH;

    // cél tábla mérete
    const targetTable = editedTables.find((t) => t.id === targetTableId);
    if (!targetTable) return;
    const [targetH, targetW] = JSON.parse(targetTable.size || "[0,0]");

    // szabad pozíció keresése
    const others = editedItems
      .flatMap((it) => it.processedPositions)
      .filter((p) => p.id === targetTableId && p.instanceId !== instanceId);
    const free = findFreePosition(
      itemWidth,
      itemHeight,
      instanceId,
      targetW,
      targetH,
      others
    );
    const [newX, newY] = free ? [free.x, free.y] : [0, 0];
    const newPos = {
      x: newX + TRIM,
      y: newY + TRIM,
      rotation,
      id: targetTableId,
      width: itemWidth,
      height: itemHeight,
      datiRowNumber: null,
      instanceId
    };

    // 1) töröljük az eredetit az eredeti táblából
    let updated = { ...selectedItem };
    updated.processedPositions = updated.processedPositions.filter(
      (p) => p.instanceId !== instanceId
    );

    // 2) hozzáadjuk az új pozíciót
    updated.processedPositions.push(newPos);

    // 3) frissítjük a tablePosition stringet is
    updated.tablePosition = updated.processedPositions
      .map((p) => {
        if (p.id == null) {
          throw new Error("tableId is null during save");
        }
        const datiRowNumber = Number(p.datiRowNumber);

        return `[${p.x},${p.y},${p.rotation},${p.id},${
          Number.isInteger(datiRowNumber) && datiRowNumber > 0
            ? datiRowNumber
            : 0
        }]`;
      })
      .join(",");

    // 4) cseréljük a listában
    const newItems = editedItems.map((it) =>
      it.itemId === itemId ? updated : it
    );
    onDirty(true);
    setEditedItems(newItems);
    setSelectedItemPosition(null);

    // végül navigálunk az új táblára
    const newIndex = editedTables.findIndex((t) => t.id === targetTableId);
    setCurrentTableIndex(newIndex);
  };
  /**
   * A toKantList által visszaadott sorokban a hossz mm-ben van.
   *
   * Például:
   * "G: 12500.0"
   *
   * ebből:
   * "G: 12.500 méter"
   */
  const formatKantListInMeters = (kantLength) => {
    return toKantList(kantLength).map((row, index) => {
      const rawRow = String(row ?? "").trim();

      const match = rawRow.match(/^(.*?)(-?\d+(?:[.,]\d+)?)\s*(?:mm)?\s*$/);

      if (!match) {
        return <span key={index}>{rawRow}</span>;
      }

      const label = match[1].trim();
      const millimeters = Number(match[2].replace(",", "."));

      if (!Number.isFinite(millimeters)) {
        return <span key={index}>{rawRow}</span>;
      }

      const meters = millimeters / 1000;

      return (
        <span key={index}>
          <strong>{label}</strong> {meters.toFixed(3)} m
        </span>
      );
    });
  };
  const workKantList = formatKantListInMeters(workInfo.kantLength);

  const tblKantList = formatKantListInMeters(tblInfo?.kantLength);

  const ToolbarButton = ({ label, title, className = "", ...props }) => (
    <button
      type="button"
      title={title || label}
      aria-label={title || label}
      className={[
        "table-viewer-component__toolbar-button",
        "table-viewer-component__toolbar-button--text",
        className
      ]
        .filter(Boolean)
        .join(" ")}
      {...props}
    >
      <span className="table-viewer-component__toolbar-button-label">
        {label}
      </span>
    </button>
  );

  const renderWorkSummaryContent = () => (
    <div className="table-viewer-component__details-table">
      <div className="table-viewer-component__details-table-title">
        {t("work")}
      </div>

      <div className="table-viewer-component__details-table-row">
        <div className="table-viewer-component__details-table-cell">
          <span className="table-viewer-component__details-table-label">
            {t("avgUsage")}:
          </span>

          <span className="table-viewer-component__details-table-value">
            {fmtNum(workInfo.tableUsage * 100, 1)}%
          </span>
        </div>

        <div className="table-viewer-component__details-table-cell">
          <span className="table-viewer-component__details-table-label">
            {t("cutLength")}:
          </span>

          <span className="table-viewer-component__details-table-value">
            {fmtNum(workInfo.cutLength, 1)} mm
          </span>
        </div>
      </div>

      <div className="table-viewer-component__details-table-row">
        <div className="table-viewer-component__details-table-cell">
          <span className="table-viewer-component__details-table-label">
            {t("partCount")}:
          </span>

          <span className="table-viewer-component__details-table-value">
            {fmtNum(workInfo.partCount, 0)}
          </span>
        </div>

        <div className="table-viewer-component__details-table-cell">
          <span className="table-viewer-component__details-table-label">
            {t("measureCount")}:
          </span>

          <span className="table-viewer-component__details-table-value">
            {fmtNum(workInfo.measureCount, 0)}
          </span>
        </div>
      </div>

      <div className="table-viewer-component__details-table-row table-viewer-component__details-table-row--full">
        <div className="table-viewer-component__details-table-cell">
          <span className="table-viewer-component__details-table-label">
            {t("kantTotal")}:
          </span>

          <div className="table-viewer-component__details-table-list">
            {workKantList.length
              ? workKantList.map((row, index) => <span key={index}>{row}</span>)
              : "–"}
          </div>
        </div>
      </div>

      {workInfo.usedTables.length > 0 && (
        <div className="table-viewer-component__details-table-row table-viewer-component__details-table-row--full">
          <div className="table-viewer-component__details-table-cell">
            <span className="table-viewer-component__details-table-label">
              {t("usedSheets")}:
            </span>

            <div className="table-viewer-component__details-table-list">
              {(() => {
                const map = new Map();

                createdTables.forEach((createdTable) => {
                  const colorId = createdTable.color?.colorId;
                  const millingId = createdTable.milling?.millingId;

                  if (!colorId) return;

                  const color = colorsFromStore.find(
                    (storedColor) => storedColor.colorId === colorId
                  );

                  const millingName = getMillingName(millingId);
                  const key = `${colorId}__${millingId}`;

                  const colorName = color ? color.name : t("unknownColor");

                  const translatedColorName = t(colorName);

                  const label = `${translatedColorName}${
                    millingName ? ` - ${millingName}` : ""
                  }`;

                  if (!map.has(key)) {
                    map.set(key, {
                      label,
                      count: 0,
                      usage: 0,
                      isMDF: false
                    });
                  }

                  const entry = map.get(key);

                  const isMDF =
                    String(color?.materialType ?? "")
                      .trim()
                      .toUpperCase() === "MDF";

                  entry.isMDF = isMDF;

                  if (isMDF) {
                    entry.usage += createdTable.tableUsage ?? 0;
                  } else {
                    /*
                     * Split tábla fél táblának számít.
                     *
                     * true  -> 0.5
                     * false -> 1
                     */
                    entry.count += createdTable.split === true ? 0.5 : 1;
                  }
                });

                return Array.from(map.values())
                  .sort((a, b) => a.label.localeCompare(b.label, "hu"))
                  .map((row, index) => (
                    <span key={index}>
                      {row.isMDF
                        ? `${fmtNum(row.usage, 3)} m² - `
                        : `${fmtNum(row.count, 1)} ${t("quantityShort")} - `}

                      <strong>{row.label}</strong>
                    </span>
                  ));
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderTableSummaryContent = () => (
    <div className="table-viewer-component__details-table">
      <div className="table-viewer-component__details-table-title">
        {t("currentTable")}
      </div>

      <div className="table-viewer-component__details-table-row table-viewer-component__details-table-row--full">
        <div className="table-viewer-component__details-table-cell">
          <span className="table-viewer-component__details-table-label">
            {t("id")}:
          </span>

          <span className="table-viewer-component__details-table-value">
            {tblInfo?.id ?? "–"}
          </span>
        </div>
      </div>

      <div className="table-viewer-component__details-table-row">
        <div className="table-viewer-component__details-table-cell">
          <span className="table-viewer-component__details-table-label">
            {t("color")}:
          </span>

          <span className="table-viewer-component__details-table-value">
            {t(tblInfo?.colorName) || "–"}
          </span>
        </div>

        <div className="table-viewer-component__details-table-cell">
          <span className="table-viewer-component__details-table-label">
            {t("milling")}:
          </span>

          <span className="table-viewer-component__details-table-value">
            {getMillingName(table?.milling?.millingId) || "–"}
          </span>
        </div>
      </div>

      <div className="table-viewer-component__details-table-row">
        <div className="table-viewer-component__details-table-cell">
          <span className="table-viewer-component__details-table-label">
            {t("usage")}:
          </span>

          <span className="table-viewer-component__details-table-value">
            {fmtNum(tblInfo?.usage * 100, 1)}%
          </span>
        </div>

        <div className="table-viewer-component__details-table-cell">
          <span className="table-viewer-component__details-table-label">
            {t("cutLength")}:
          </span>

          <span className="table-viewer-component__details-table-value">
            {fmtNum(tblInfo?.cutLength, 1)} mm
          </span>
        </div>
      </div>

      <div className="table-viewer-component__details-table-row">
        <div className="table-viewer-component__details-table-cell">
          <span className="table-viewer-component__details-table-label">
            {t("partCount")}:
          </span>

          <span className="table-viewer-component__details-table-value">
            {fmtNum(tblInfo?.partCount, 0)}
          </span>
        </div>

        <div className="table-viewer-component__details-table-cell">
          <span className="table-viewer-component__details-table-label">
            {t("measureCount")}:
          </span>

          <span className="table-viewer-component__details-table-value">
            {fmtNum(tblInfo?.measureCount, 0)}
          </span>
        </div>
      </div>

      <div className="table-viewer-component__details-table-row table-viewer-component__details-table-row--full">
        <div className="table-viewer-component__details-table-cell">
          <span className="table-viewer-component__details-table-label">
            {t("kant")}:
          </span>

          <div className="table-viewer-component__details-table-list">
            {tblKantList.length
              ? tblKantList.map((row, index) => <span key={index}>{row}</span>)
              : "–"}
          </div>
        </div>
      </div>
    </div>
  );

  const renderCanvas = () => {
    const scaledDrawingWidth = width * scaleFactor;
    const scaledDrawingHeight = height * scaleFactor;
    const renderedDrawingWidth = Math.round(scaledDrawingWidth);
    const renderedDrawingHeight = Math.round(scaledDrawingHeight);
    const ITEM_MASK_BLEED = 1;
    const calculatedWasteRects = computeWasteRects(renderItems, table, {
      kerf: KERF,
      trim: TRIM,
      outerWidth: width,
      outerHeight: height,
      minWidth: 1,
      minHeight: 1,
      minArea: 1
    });

    const tableHasPlacedItems = renderItems.some((item) =>
      (item.processedPositions || []).some(
        (position) => String(position.id) === String(table?.id)
      )
    );

    const tableHasCutLines = (() => {
      try {
        const { horizontals = [], verticals = [] } = parseRigheAbs(table);

        return horizontals.length > 0 || verticals.length > 0;
      } catch {
        return false;
      }
    })();

    const safeTrim = Math.max(0, Number(TRIM) || 0);

    /*
     * Fontos:
     * Üres táblánál a fallback waste-et abból a koordinátarendszerből
     * kell számolni, amiben az SVG is rajzol.
     *
     * Nálad ez a width / height, nem a tableSize nyers értéke.
     */
    const safeTableWidth = Number(width);
    const safeTableHeight = Number(height);

    const innerWasteWidth = safeTableWidth - safeTrim;
    const innerWasteHeight = safeTableHeight - safeTrim;

    const emptyTableWasteRects =
      !tableHasPlacedItems &&
      !tableHasCutLines &&
      calculatedWasteRects.length === 0 &&
      innerWasteWidth > 0 &&
      innerWasteHeight > 0
        ? [
            {
              instanceId: `empty-table-waste-${table?.id ?? "new"}`,
              x: safeTrim,
              y: safeTrim,
              width: innerWasteWidth,
              height: innerWasteHeight,
              isEmptyTableWaste: true
            }
          ]
        : [];

    const wasteRects =
      calculatedWasteRects.length > 0
        ? calculatedWasteRects
        : emptyTableWasteRects;
    const safeTableId = String(table?.id ?? "table").replace(
      /[^a-zA-Z0-9_-]/g,
      "-"
    );
    const itemMaskId = `item-mask-${safeTableId}`;

    const wastePatternId = `waste-pattern-${safeTableId}`;
    const isRotatedTable = tableRotation === -90 || tableRotation === 90;

    const visualWidth = isRotatedTable
      ? renderedDrawingHeight
      : renderedDrawingWidth;

    const visualHeight = isRotatedTable
      ? renderedDrawingWidth
      : renderedDrawingHeight;

    return (
      <div ref={canvasAreaRef} className="table-viewer-component__canvas-area">
        <div
          className="table-viewer-component__drawing-frame"
          style={{
            "--tvc-drawing-frame-width": `${visualWidth}px`,
            "--tvc-drawing-frame-height": `${visualHeight}px`
          }}
        >
          <div
            ref={drawingRef}
            className={
              isEditing
                ? "table-viewer-component__drawing table-viewer-component__drawing--editing"
                : "table-viewer-component__drawing"
            }
            style={{
              "--tvc-drawing-width": `${renderedDrawingWidth}px`,
              "--tvc-drawing-height": `${renderedDrawingHeight}px`,
              "--tvc-drawing-border-width": `${BORDER_WIDTH}px`,
              "--tvc-drawing-rotation": `${tableRotation}deg`,
              "--tvc-drawing-bg": showBackground
                ? backgroundImageStyle
                : "none",
              backgroundImage: showBackground ? backgroundImageStyle : "none"
            }}
          >
            <div
              className="table-viewer-component__dimension-label table-viewer-component__dimension-label--width"
              style={{
                "--tvc-dimension-rotation": `${tableRotation === -90 ? 90 : 0}deg`
              }}
            >
              {width1 > 0 ? width1 + 2 * displayTrim : "Nincs méret"}
            </div>

            <div
              className="table-viewer-component__dimension-label table-viewer-component__dimension-label--height"
              style={{
                "--tvc-dimension-rotation": `${tableRotation === -90 ? 90 : 0}deg`
              }}
            >
              {height1 > 0 ? height1 + 2 * displayTrim : "Nincs méret"}
            </div>
            <svg
              className="table-viewer-component__overlay-svg"
              width={renderedDrawingWidth}
              height={renderedDrawingHeight}
              overflow="hidden"
            >
              <defs>
                <mask
                  id={itemMaskId}
                  maskUnits="userSpaceOnUse"
                  maskContentUnits="userSpaceOnUse"
                  x={0}
                  y={0}
                  width={renderedDrawingWidth}
                  height={renderedDrawingHeight}
                >
                  <rect
                    x={0}
                    y={0}
                    width={renderedDrawingWidth}
                    height={renderedDrawingHeight}
                    fill="white"
                  />

                  {filteredItems.map((item) =>
                    (item.processedPositions || [])
                      .filter((pos) => pos.id === table.id)
                      .map((pos, index) => {
                        const {
                          x,
                          y,
                          width: itemWidth,
                          height: itemHeight
                        } = pos;

                        const scaledX = x * scaleFactor;
                        const scaledY = y * scaleFactor;
                        const scaledWidth = itemWidth * scaleFactor;
                        const scaledHeight = itemHeight * scaleFactor;

                        return (
                          <rect
                            key={`${item.itemId}-${pos.instanceId}`}
                            x={scaledX - ITEM_MASK_BLEED}
                            y={scaledY - ITEM_MASK_BLEED}
                            width={scaledWidth + ITEM_MASK_BLEED * 2}
                            height={scaledHeight + ITEM_MASK_BLEED * 2}
                            fill="black"
                          />
                        );
                      })
                  )}
                </mask>
              </defs>
              <g mask={`url(#${itemMaskId})`}>
                {" "}
                {memoizedDiagonalLines.map((line, index) => (
                  <line
                    key={index}
                    x1={line.x1}
                    y1={line.y1}
                    x2={line.x2}
                    y2={line.y2}
                    className="table-viewer-component__diagonal-line"
                  />
                ))}
              </g>
              <g className="table-viewer-component__waste-layer">
                {wasteRects.map((waste) => {
                  const rawX = waste.x * scaleFactor;
                  const rawY = waste.y * scaleFactor;
                  const rawWidth = waste.width * scaleFactor;
                  const rawHeight = waste.height * scaleFactor;

                  const rectX = rawX;
                  const rectY = rawY;
                  const rectWidth = Math.max(0, rawWidth);
                  const rectHeight = Math.max(0, rawHeight);

                  if (rectWidth <= 0 || rectHeight <= 0) return null;
                  const collapseInfo = getWasteCollapseInfo(waste);
                  const isWasteTooSmall =
                    rectWidth < DETAIL_THRESHOLD ||
                    rectHeight < DETAIL_THRESHOLD;
                  const widthLabelX = rectX + rectWidth / 2;
                  const widthLabelY = rectY + rectHeight - 20;

                  const heightLabelX = rectX + 10;
                  const heightLabelY = rectY + rectHeight / 2;

                  const labelRotation =
                    tableRotation === -90 ? 90 : tableRotation === 90 ? -90 : 0;

                  return (
                    <g
                      key={waste.instanceId}
                      className={[
                        "table-viewer-component__waste-piece",
                        !isWasteTooSmall &&
                          "table-viewer-component__waste-piece--show-dimensions",
                        placementItemId != null &&
                          "table-viewer-component__waste-piece--placement-target"
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      role={placementItemId != null ? "button" : undefined}
                      tabIndex={placementItemId != null ? 0 : undefined}
                      onClick={(event) => {
                        if (placementItemId == null) return;

                        event.preventDefault();
                        event.stopPropagation();
                        event.currentTarget.blur();
                        handlePlaceItemInWaste(waste);
                      }}
                      onKeyDown={(event) => {
                        if (placementItemId == null) return;
                        if (event.key !== "Enter" && event.key !== " ") return;

                        event.preventDefault();
                        event.stopPropagation();
                        event.currentTarget.blur();
                        handlePlaceItemInWaste(waste);
                      }}
                      style={{
                        cursor:
                          placementItemId != null ? "crosshair" : undefined
                      }}
                    >
                      <rect
                        x={rectX}
                        y={rectY}
                        width={rectWidth}
                        height={rectHeight}
                        fill={`url(#${wastePatternId})`}
                        stroke="#000"
                        strokeWidth="1"
                        vectorEffect="non-scaling-stroke"
                        className="table-viewer-component__waste-rect"
                        pointerEvents={
                          placementItemId != null ? "all" : undefined
                        }
                      />
                      <rect
                        x={rectX}
                        y={rectY}
                        width={rectWidth}
                        height={rectHeight}
                        fill="#000000"
                        className="table-viewer-component__waste-hover-background"
                        pointerEvents="none"
                      />
                      {collapseInfo && !cannotEdit && (
                        <foreignObject
                          x={rectX + 4}
                          y={rectY + 4}
                          width="28"
                          height="28"
                          className="table-viewer-component__waste-collapse-control"
                        >
                          <button
                            type="button"
                            className="table-viewer-component__waste-collapse-button"
                            title="Szektorok összecsúsztatása"
                            aria-label="Szektorok összecsúsztatása"
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              handleCollapseWaste(waste, collapseInfo);
                            }}
                          >
                            <IonIcon icon={collapseInfo.icon} />
                          </button>
                        </foreignObject>
                      )}
                      {/* Szélesség */}
                      <text
                        x={widthLabelX}
                        y={widthLabelY}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        transform={`rotate(
              ${labelRotation},
              ${widthLabelX},
              ${widthLabelY}
            )`}
                        className={[
                          "table-viewer-component__waste-dimension",
                          "table-viewer-component__waste-dimension--width"
                        ].join(" ")}
                      >
                        {fmtNum(waste.width, 1)}
                      </text>

                      {/* Magasság */}
                      <text
                        x={heightLabelX}
                        y={heightLabelY}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        transform={`rotate(
              ${labelRotation},
              ${heightLabelX},
              ${heightLabelY}
            )`}
                        className={[
                          "table-viewer-component__waste-dimension",
                          "table-viewer-component__waste-dimension--height"
                        ].join(" ")}
                      >
                        {fmtNum(waste.height, 1)}
                      </text>
                    </g>
                  );
                })}
              </g>

              <g className="table-viewer-component__item-border-layer">
                {filteredItems.flatMap((item) =>
                  (item.processedPositions || [])
                    .filter((pos) => pos.id === table.id)
                    .flatMap((pos) => {
                      const trimTolerance = 0.01;
                      const touchesLeftTrim =
                        Math.abs(pos.x - TRIM) <= trimTolerance;
                      const touchesTopTrim =
                        Math.abs(pos.y - TRIM) <= trimTolerance;
                      const left = pos.x * scaleFactor;
                      const top = pos.y * scaleFactor;
                      const right = (pos.x + pos.width) * scaleFactor;
                      const bottom = (pos.y + pos.height) * scaleFactor;

                      return [
                        <rect
                          key={`border-${item.itemId}-${pos.instanceId}`}
                          x={left}
                          y={top}
                          width={right - left}
                          height={bottom - top}
                          fill="none"
                          stroke="#000"
                          strokeWidth="1"
                          vectorEffect="non-scaling-stroke"
                        />,
                        touchesLeftTrim && (
                          <line
                            key={`trim-left-${item.itemId}-${pos.instanceId}`}
                            x1={left}
                            y1={top}
                            x2={left}
                            y2={bottom}
                            stroke="#000"
                            strokeWidth="1.5"
                            vectorEffect="non-scaling-stroke"
                          />
                        ),
                        touchesTopTrim && (
                          <line
                            key={`trim-top-${item.itemId}-${pos.instanceId}`}
                            x1={left}
                            y1={top}
                            x2={right}
                            y2={top}
                            stroke="#000"
                            strokeWidth="1.5"
                            vectorEffect="non-scaling-stroke"
                          />
                        )
                      ];
                    })
                )}
              </g>
            </svg>

            {filteredItems.map((item) => {
              return (item.processedPositions || [])
                .filter((pos) => pos.id === table.id)
                .map((pos) => {
                  const {
                    x = 0,
                    y = 0,
                    rotation,
                    width: itemWidth,
                    height: itemHeight
                  } = pos;

                  const MIN_PX = 5;
                  const leftPx = x * scaleFactor;
                  const topPx = y * scaleFactor;
                  const dispWpx = Math.max(itemWidth * scaleFactor, MIN_PX);
                  const dispHpx = Math.max(itemHeight * scaleFactor, MIN_PX);
                  const scaledW = dispWpx;
                  const scaledH = dispHpx;

                  const isTooSmall =
                    scaledW < DETAIL_THRESHOLD || scaledH < DETAIL_THRESHOLD;

                  const isVerySmall =
                    scaledW < VERY_DETAIL_THRESHOLD ||
                    scaledH < VERY_DETAIL_THRESHOLD;

                  const fontSizePx = Math.max(
                    6,
                    Math.min(12, Math.min(dispWpx, dispHpx) / 5)
                  );

                  const paddingPx = fontSizePx / 4;
                  const positionKey = `${item.itemId}-${pos.instanceId}`;

                  const isSelected = selectedItemPositions.some(
                    (selection) =>
                      selection.itemId === item.itemId &&
                      selection.instanceId === pos.instanceId
                  );

                  const dispWidth = itemWidth;
                  const dispHeight = itemHeight;
                  const isRotated = rotation == 1;

                  let kantCode = "";
                  let kantLenCount = 0;
                  let kantWidCount = 0;

                  if (Array.isArray(item.kant)) {
                    [kantCode, kantLenCount, kantWidCount] = item.kant;
                  } else if (typeof item.kant === "string") {
                    const parts = item.kant
                      .replace(/^\[|\]$/g, "")
                      .split(",")
                      .map((value) => value.trim());

                    kantCode = parts[0] || "";
                    kantLenCount = parseInt(parts[1], 10) || 0;
                    kantWidCount = parseInt(parts[2], 10) || 0;
                  }

                  const kantMap = {
                    "": "-",
                    "04": "S",
                    2: "G",
                    42: "LAT",
                    1: "L"
                  };

                  const kantChar = kantMap[String(kantCode)] || "";
                  const widthSuffix = kantChar.repeat(kantWidCount);
                  const heightSuffix = kantChar.repeat(kantLenCount);

                  const textAngle = (isRotated ? 0 : 0) - tableRotation;

                  return (
                    <div
                      key={positionKey}
                      onMouseDown={(event) => {
                        if (event.button !== 0) return;
                        if (!event.shiftKey) return;

                        event.preventDefault();

                        const selectionMode = isSelected
                          ? "deselect"
                          : "select";

                        shiftSelectingRef.current = true;
                        shiftSelectionModeRef.current = selectionMode;
                        suppressNextClickRef.current = true;

                        if (selectionMode === "deselect") {
                          removeItemFromSelection(item.itemId, pos.instanceId);
                        } else {
                          addItemToSelection(item.itemId, pos.instanceId);
                        }
                      }}
                      onMouseEnter={(event) => {
                        const leftButtonIsPressed = (event.buttons & 1) === 1;

                        if (!shiftSelectingRef.current) return;
                        if (!leftButtonIsPressed) return;

                        event.preventDefault();

                        if (shiftSelectionModeRef.current === "deselect") {
                          removeItemFromSelection(item.itemId, pos.instanceId);
                        } else {
                          addItemToSelection(item.itemId, pos.instanceId);
                        }
                      }}
                      onClick={(event) => {
                        /*
                         * Shift-húzás után ne fusson le még egyszer
                         * a normál kattintásos toggle.
                         */
                        if (suppressNextClickRef.current || event.shiftKey) {
                          suppressNextClickRef.current = false;
                          return;
                        }

                        toggleItemSelection(item.itemId, pos.instanceId);
                        const measuredRect =
                          event.currentTarget.getBoundingClientRect();
                        setDebugRectInfo({
                          itemId: item.itemId,
                          x,
                          y,
                          width: itemWidth,
                          height: itemHeight,
                          rectWidth: measuredRect.width,
                          rectHeight: measuredRect.height,
                          rectLeft: measuredRect.left,
                          rectTop: measuredRect.top
                        });
                      }}
                      onDragStart={(event) => {
                        event.preventDefault();
                      }}
                      className={[
                        "table-viewer-component__item",
                        isSelected && "table-viewer-component__item--selected",
                        isTooSmall && "table-viewer-component__item--small",
                        isVerySmall &&
                          "table-viewer-component__item--very-small"
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      style={{
                        "--tvc-item-left": `${leftPx}px`,
                        "--tvc-item-top": `${topPx}px`,
                        "--tvc-item-width": `${dispWpx}px`,
                        "--tvc-item-height": `${dispHpx}px`,
                        "--tvc-item-cursor": "pointer",
                        userSelect: "none",
                        WebkitUserSelect: "none"
                      }}
                    >
                      {!cannotEdit && (
                        <button
                          type="button"
                          className="table-viewer-component__item-delete"
                          title="Pozíció törlése"
                          aria-label="Pozíció törlése"
                          style={{
                            "--tvc-item-delete-rotation": `${-tableRotation}deg`
                          }}
                          onMouseDown={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                          }}
                          onClick={(event) =>
                            handleRemoveItemPosition(
                              event,
                              item.itemId,
                              pos.instanceId
                            )
                          }
                        >
                          <IonIcon icon={trashOutline} />
                        </button>
                      )}

                      {!isTooSmall && (
                        <div
                          className="table-viewer-component__item-label"
                          style={{
                            "--tvc-item-text-rotation": `${textAngle}deg`
                          }}
                        >
                          <span
                            className="table-viewer-component__item-id-badge"
                            style={{
                              "--tvc-item-id-font-size": `${fontSizePx}px`,
                              "--tvc-item-id-padding": `${paddingPx}px`
                            }}
                          >
                            {item.itemId}
                          </span>

                          <br />

                          <span className="table-viewer-component__item-detail-text">
                            {item.details}
                          </span>
                        </div>
                      )}

                      {!isVerySmall && isTooSmall && (
                        <div
                          className="table-viewer-component__item-label table-viewer-component__item-label--small"
                          style={{
                            "--tvc-item-text-rotation": `${textAngle}deg`
                          }}
                        >
                          {item.details}
                        </div>
                      )}

                      <div
                        className="table-viewer-component__item-dimension table-viewer-component__item-dimension--width"
                        style={{
                          "--tvc-item-text-rotation": `${textAngle}deg`,
                          "--tvc-item-dimension-font-size": isVerySmall
                            ? "8px"
                            : "10px",
                          "--tvc-item-dimension-line-height": isVerySmall
                            ? "1"
                            : "normal",
                          "--tvc-item-width-margin-top":
                            tableRotation === 90
                              ? "0"
                              : isVerySmall
                                ? "3px"
                                : "2px",
                          "--tvc-item-dimension-padding": isVerySmall
                            ? "0px"
                            : "2px"
                        }}
                      >
                        <span className="table-viewer-component__item-dimension-value">
                          {dispWidth}
                        </span>

                        {!isVerySmall && widthSuffix && (
                          <>
                            <br />
                            <span className="table-viewer-component__item-dimension-suffix">
                              {widthSuffix}
                            </span>
                          </>
                        )}
                      </div>

                      <div
                        className="table-viewer-component__item-dimension table-viewer-component__item-dimension--height"
                        style={{
                          "--tvc-item-text-rotation": `${textAngle}deg`,
                          "--tvc-item-dimension-font-size": isVerySmall
                            ? "8px"
                            : "10px",
                          "--tvc-item-dimension-line-height": isVerySmall
                            ? "1"
                            : "normal"
                        }}
                      >
                        <span className="table-viewer-component__item-dimension-value">
                          {dispHeight}
                        </span>

                        {!isVerySmall && heightSuffix && (
                          <>
                            <br />
                            <span className="table-viewer-component__item-dimension-suffix">
                              {heightSuffix}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  );
                });
            })}

            {/*debugRectInfo && (
              <div
                className="table-viewer-component__debug-rect-info"
                style={{
                  "--tvc-debug-popup-rotation": `${-tableRotation}deg`
                }}
              >
                <strong>Elem: {debugRectInfo.itemId}</strong>
                <span>
                  Pozíció: x={debugRectInfo.x}, y={debugRectInfo.y}
                </span>
                <span>
                  Modell rect: {debugRectInfo.width} × {debugRectInfo.height}
                </span>
                <span>
                  Mért rect: {debugRectInfo.rectWidth.toFixed(2)} ×{" "}
                  {debugRectInfo.rectHeight.toFixed(2)} px
                </span>
                <span>
                  Mért hely: {debugRectInfo.rectLeft.toFixed(2)},{" "}
                  {debugRectInfo.rectTop.toFixed(2)} px
                </span>
              </div>
            )*/}
          </div>
        </div>
      </div>
    );
  };

  const renderOverlays = () => (
    <>
      {isExporting && (
        <div className="table-viewer-component__export-overlay">
          <Loading />
        </div>
      )}

      {statusMessage && (
        <div
          className={`table-viewer-component__status table-viewer-component__status--${
            statusMessage.type || "info"
          }`}
        >
          {statusMessage.text}
        </div>
      )}
    </>
  );
  const renderUnifiedLayout = () => (
    <div className="table-viewer-component__layout">
      {/* Felső szöveges toolbar */}
      <header className="table-viewer-component__main-toolbar">
        <div className="table-viewer-component__toolbar-group">
          {!cannotEdit && (
            <ToolbarButton
              label={t("save")}
              title={t("save")}
              onClick={handlePersistChanges}
              disabled={!hasDirty}
              className="table-viewer-component__toolbar-button--success"
            />
          )}

          {!cannotEdit && (
            <ToolbarButton
              label={t("generateBtn")}
              title={t("generateBtn")}
              onClick={onGenerate}
              className="table-viewer-component__toolbar-button--success"
            />
          )}

          {!isEditing && (
            <ToolbarButton
              label={t("exportBtn")}
              title={t("exportBtn")}
              onClick={() => setShowExportModal(true)}
              className="table-viewer-component__toolbar-button--primary"
            />
          )}

          {!isEditing && (
            <ToolbarButton
              label={t("sizeListBtn")}
              title={t("sizeListBtn")}
              onClick={onSizeList}
              className="table-viewer-component__toolbar-button--primary"
            />
          )}
        </div>

        {/*!cannotEdit && isEditing && (
      
        )*/}
        <div className="table-viewer-component__toolbar-group">
          {canAddHalfTable ? (
            <Dropdown className="table-viewer-component__add-table-dropdown">
              <Dropdown.Toggle
                id="add-table-dropdown"
                className={[
                  "table-viewer-component__toolbar-button",
                  "table-viewer-component__toolbar-button--text",
                  "table-viewer-component__toolbar-button--primary",
                  "table-viewer-component__add-table-toggle"
                ].join(" ")}
              >
                <span className="table-viewer-component__toolbar-button-label">
                  {t("addTable")}
                </span>
              </Dropdown.Toggle>

              <Dropdown.Menu>
                <Dropdown.Item
                  onClick={() => openAddTableModal(currentFullDimension, false)}
                >
                  <strong> {t("fullTable")}</strong>

                  <small className="table-viewer-component__add-table-size">
                    {formatTableDimension(currentFullDimension)}
                  </small>
                </Dropdown.Item>

                <Dropdown.Item
                  onClick={() => openAddTableModal(currentSplitDimension, true)}
                >
                  <strong> {t("halfTable")}</strong>

                  <small className="table-viewer-component__add-table-size">
                    {formatTableDimension(currentSplitDimension)}
                  </small>
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          ) : (
            <ToolbarButton
              label={t("addTable")}
              title={t("addTable")}
              onClick={handleAddTable}
              className="table-viewer-component__toolbar-button--primary"
            />
          )}

          <ToolbarButton
            label={t("deleteTable")}
            title={t("deleteTable")}
            onClick={handleDeleteTable}
            className="table-viewer-component__toolbar-button--danger"
          />
        </div>
        <div className="table-viewer-component__toolbar-group table-viewer-component__toolbar-group--end">
          <ToolbarButton
            label={showBackground ? t("bgOff") : t("bgOn")}
            title={showBackground ? t("bgOff") : t("bgOn")}
            onClick={toggleBackground}
            className={[
              "table-viewer-component__toolbar-button--warning",
              !showBackground &&
                "table-viewer-component__toolbar-button--inactive"
            ]
              .filter(Boolean)
              .join(" ")}
          />
        </div>
      </header>

      {/* Fix méretű lapozósáv közvetlenül a toolbar alatt */}
      <nav className="table-viewer-component__top-navigation">
        {/* Színlapozó */}
        <div className="table-viewer-component__pager table-viewer-component__pager--color">
          <button
            type="button"
            className="table-viewer-component__pager-button"
            title="Előző szín"
            aria-label="Előző szín"
            onClick={() => changeColor(-1)}
            disabled={tablePalette.length <= 1}
          >
            <IonIcon icon={chevronBackOutline} />
          </button>

          <div
            className="table-viewer-component__pager-value table-viewer-component__pager-value--color"
            title={activeColorPaginationLabel}
          >
            {activeColorPaginationLabel}
          </div>

          <button
            type="button"
            className="table-viewer-component__pager-button"
            title="Következő szín"
            aria-label="Következő szín"
            onClick={() => changeColor(1)}
            disabled={tablePalette.length <= 1}
          >
            <IonIcon icon={chevronForwardOutline} />
          </button>
        </div>

        {/* Táblalapozó */}
        <div className="table-viewer-component__pager table-viewer-component__pager--table">
          <button
            type="button"
            className="table-viewer-component__pager-button"
            title="Előző tábla"
            aria-label="Előző tábla"
            onClick={onPrev}
            disabled={!showNav}
          >
            <IonIcon icon={chevronBackOutline} />
          </button>

          <div
            className="table-viewer-component__pager-value table-viewer-component__pager-value--table"
            title="Aktuális tábla"
          >
            {table?.id ?? "–"}
          </div>

          <button
            type="button"
            className="table-viewer-component__pager-button"
            title="Következő tábla"
            aria-label="Következő tábla"
            onClick={onNext}
            disabled={!showNav}
          >
            <IonIcon icon={chevronForwardOutline} />
          </button>
        </div>
      </nav>

      {/* Canvas */}
      <main className="table-viewer-component__workspace">
        {renderCanvas()}
      </main>

      {/* Alsó részletek */}
      <footer className="table-viewer-component__details-footer">
        <section className="table-viewer-component__details-section">
          {renderWorkSummaryContent()}
        </section>

        <section className="table-viewer-component__details-section">
          {renderTableSummaryContent()}
        </section>
      </footer>

      {renderOverlays()}
    </div>
  );
  return (
    <div className="table-viewer-component">
      <Modal show={showLeaveGuard} onHide={() => setShowLeaveGuard(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t("unsavedChangesTitle")}</Modal.Title>
        </Modal.Header>

        <Modal.Body>{t("unsavedChangesBody")}</Modal.Body>

        <Modal.Footer>
          <Button
            variant="primary"
            onClick={async () => {
              try {
                await saveGeneratedTables();
                await saveDirtyItemsIfAny(false);
                setShowLeaveGuard(false);

                if (pendingPath == null) {
                  setIsEditing(false);
                  setShowLeaveGuard(false);
                } else {
                  const to = pendingPath;
                  setPendingPath(null);
                  navigate(to);
                }
              } catch (e) {
                console.error(e);
                alert(t("saveFailed"));
              }
            }}
          >
            {t("saveAndContinue")}
          </Button>

          <Button
            variant="danger"
            onClick={() => {
              onDirty(false);
              setShowLeaveGuard(false);

              if (pendingPath == null) {
                setIsEditing(false);
                setShowLeaveGuard(false);
              } else {
                const to = pendingPath;
                setPendingPath(null);
                navigate(to);
              }
            }}
          >
            {t("discardChanges")}
          </Button>

          <Button
            variant="secondary"
            onClick={() => {
              setPendingPath(null);
              setShowLeaveGuard(false);
            }}
          >
            {t("cancel")}
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={addTableModal.show} onHide={closeAddTableModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("newTableSettings")}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div style={{ display: "grid", gap: "16px" }}>
            <div>
              <strong>
                {addTableModal.isHalfTable ? t("halfTable") : t("fullTable")}
              </strong>

              <div style={{ opacity: 0.75 }}>
                {t("dimension")}: {formatTableDimension(addTableModal.size)}
              </div>
            </div>

            <Form.Group>
              <Form.Label>{t("trim")}</Form.Label>
              <Form.Control
                type="number"
                min="0"
                step="1"
                value={newTableTrim}
                onChange={(event) => setNewTableTrim(event.target.value)}
              />

              <Form.Text>{t("trimHelp")}</Form.Text>
            </Form.Group>

            <Form.Group>
              <Form.Label>{t("cutOrientation")}</Form.Label>
              <Form.Select
                value={newTableCutOrientation ? "vertical" : "horizontal"}
                onChange={(event) =>
                  setNewTableCutOrientation(event.target.value === "vertical")
                }
              >
                <option value="horizontal">{t("horizontal")}</option>
                <option value="vertical">{t("vertical")}</option>{" "}
              </Form.Select>
            </Form.Group>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={closeAddTableModal}>
            Mégsem
          </Button>

          <Button variant="primary" onClick={handleConfirmAddTable}>
            Tábla hozzáadása
          </Button>
        </Modal.Footer>
      </Modal>
      {renderUnifiedLayout()}{" "}
    </div>
  );
});
